/**
 * Bloq Advanced Bookings hub.
 * Tabs: Dashboard · Bookings · Booking Links · Appointment Types · Availability · Forms · Add-ons · Coupons · Analytics
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Plus, Copy, ExternalLink, CalendarClock, Sparkles, Layers, Tag, FileText,
  CheckCircle2, XCircle, Clock, BarChart3, AlertCircle, Trash2, Pencil, ChevronRight,
  MapPin, GripVertical, ChevronUp, ChevronDown, MoreVertical, Link as LinkIcon,
  CheckSquare, Square,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { RichTextEditor } from "@/components/RichTextEditor";
import { AddressFields, EMPTY_ADDRESS, composeAddress } from "@/components/AddressFields";
import ContactAutocomplete from "@/components/ContactAutocomplete";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { COMMON_TIMEZONES, userBrowserTimezone, formatInTz } from "@/lib/time";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BulkSelectToolbar, BulkDeleteConfirmDialog, useBulkSelect,
} from "@/components/BulkSelect";
import ProTip from "@/components/ProTip";
import CalendarConnectionsPanel from "@/components/CalendarConnectionsPanel";
import PaymentsPanel from "@/pages/bookings/PaymentsPanel";
import ClientsPanel from "@/pages/bookings/ClientsPanel";

const DAYS = [
  { v: 1, l: "Mon" }, { v: 2, l: "Tue" }, { v: 3, l: "Wed" },
  { v: 4, l: "Thu" }, { v: 5, l: "Fri" }, { v: 6, l: "Sat" }, { v: 7, l: "Sun" },
];

const LOCATION_LABEL = {
  bloq_video: "Bloq video meeting",
  phone: "Phone call",
  in_person: "In person",
  hybrid: "Hybrid (video + in person)",
  custom: "Custom location",
  external_link: "External link",
  tbd: "To be determined",
};

const LOCATION_NEEDS_ADDRESS = new Set(["in_person", "hybrid", "custom"]);

export default function BookingsHub() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  // Deep-link support: when a `bookingId` query param is present (the
  // dashboard's "Open" button / "Open details" menu wires this up), open
  // the Bookings tab and auto-select that booking so the drawer pops
  // immediately. Clearing the param happens once the drawer is closed.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBookingId = searchParams.get("bookingId") || "";
  const initialTab = searchParams.get("tab") || (initialBookingId ? "bookings" : "dashboard");
  const [tab, setTab] = useState(initialTab);
  // Re-sync if the URL changes while the page is already mounted.
  useEffect(() => {
    if (initialBookingId) setTab("bookings");
  }, [initialBookingId]);
  // ?tab=payments (or any other key) — let URL navigation drive the
  // active tab so the Stripe Connect return URL lands the user back
  // on Payments instead of Dashboard.
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTab(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const clearBookingId = () => {
    if (!searchParams.get("bookingId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("bookingId");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex flex-col gap-7" data-testid="bookings-hub">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="bloq-eyebrow">Bookings</p>
          <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Run a real scheduling system.</h1>
          <p className="text-sm text-[#4a4a52] mt-2 max-w-2xl">
            Advanced availability, appointment types, intake forms, approval flows, reschedule + cancel — fully wired to Bloq video meetings and your contacts.
          </p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="links" data-testid="tab-links">Links</TabsTrigger>
          <TabsTrigger value="appointment-types" data-testid="tab-apt-types">Appointment Types</TabsTrigger>
          <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="forms" data-testid="tab-forms">Forms</TabsTrigger>
          <TabsTrigger value="addons" data-testid="tab-addons">Add-ons</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-5"><DashboardPanel workspace={currentWorkspace} user={user} /></TabsContent>
        <TabsContent value="clients" className="mt-5"><ClientsPanel workspace={currentWorkspace} /></TabsContent>
        <TabsContent value="bookings" className="mt-5">
          <BookingsListPanel
            workspace={currentWorkspace}
            user={user}
            initialBookingId={initialBookingId}
            onBookingDrawerClose={clearBookingId}
          />
        </TabsContent>
        <TabsContent value="links" className="mt-5"><LinksPanel workspace={currentWorkspace} /></TabsContent>
        <TabsContent value="appointment-types" className="mt-5"><AppointmentTypesPanel workspace={currentWorkspace} /></TabsContent>
        <TabsContent value="availability" className="mt-5"><AvailabilityPanel workspace={currentWorkspace} user={user} /></TabsContent>
        <TabsContent value="payments" className="mt-5"><PaymentsPanel workspace={currentWorkspace} /></TabsContent>
        <TabsContent value="forms" className="mt-5"><FormsPanel workspace={currentWorkspace} /></TabsContent>
        <TabsContent value="addons" className="mt-5"><AddonsPanel workspace={currentWorkspace} /></TabsContent>
        <TabsContent value="coupons" className="mt-5"><CouponsPanel workspace={currentWorkspace} /></TabsContent>
        <TabsContent value="analytics" className="mt-5"><AnalyticsPanel workspace={currentWorkspace} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* =====================================================================
 *  DASHBOARD
 * ===================================================================== */
function DashboardPanel({ workspace, user }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const userTz = user?.timezone || userBrowserTimezone();
  const reload = () => {
    if (!workspace) return;
    api.get(`/booking/dashboard`, { params: { workspace_id: workspace.workspace_id } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [workspace]);
  if (!data) return <p className="text-sm text-[#82828c]">Loading…</p>;
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="bloq-card p-5 lg:col-span-2 flex flex-col gap-4" data-testid="dashboard-upcoming">
        <div className="flex items-center justify-between">
          <p className="bloq-headline text-xl text-[#005e7a]">Upcoming bookings</p>
          <span className="bloq-pill">{data.upcoming_bookings?.length || 0}</span>
        </div>
        {data.upcoming_bookings?.length ? (
          <div className="flex flex-col divide-y divide-[rgba(26,26,31,0.05)]">
            {data.upcoming_bookings.map((b) => (
              <BookingRow key={b.booking_id} b={b} tz={userTz} onOpen={() => setSelected(b)} />
            ))}
          </div>
        ) : <p className="text-sm text-[#82828c]">No upcoming bookings. Share a booking link to get started.</p>}
      </div>

      <div className="flex flex-col gap-5">
        <StatCard label="This week" value={data.volume_this_week} icon={CalendarClock} />
        <StatCard label="This month" value={data.volume_this_month} icon={BarChart3} />
        <StatCard
          label="Paid revenue"
          value={data.stripe_configured ? `$${(data.paid_revenue_total || 0).toFixed(0)}` : "Setup required"}
          icon={Sparkles}
          warning={!data.stripe_configured}
        />
      </div>

      <div className="bloq-card p-5 lg:col-span-2 flex flex-col gap-3" data-testid="dashboard-pending">
        <div className="flex items-center justify-between">
          <p className="bloq-headline text-xl text-[#005e7a]">Pending approvals</p>
          <span className="bloq-pill" style={{ background: data.pending_approvals?.length ? "#005e7a" : "#f5efe6", color: data.pending_approvals?.length ? "#fff" : "#82828c" }}>
            {data.pending_approvals?.length || 0}
          </span>
        </div>
        {data.pending_approvals?.length ? (
          data.pending_approvals.map((b) => <BookingRow key={b.booking_id} b={b} tz={userTz} showApprove onOpen={() => setSelected(b)} />)
        ) : <p className="text-sm text-[#82828c]">Nothing waiting on you.</p>}
      </div>

      <div className="bloq-card p-5 flex flex-col gap-3" data-testid="dashboard-top-links">
        <p className="bloq-headline text-lg text-[#005e7a]">Top booking links</p>
        {data.top_booking_links?.length ? data.top_booking_links.map((row, i) => (
          <div key={i} className="flex items-center justify-between bg-[#f5efe6] p-3 rounded-md">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{row.link?.title || "—"}</p>
              <p className="text-xs text-[#82828c]">{row.link?.duration_minutes}m · {row.link?.timezone}</p>
            </div>
            <span className="bloq-pill">{row.count}</span>
          </div>
        )) : <p className="text-sm text-[#82828c]">Once people start booking, your top links show up here.</p>}
      </div>

      <div className="bloq-card p-5 flex flex-col gap-3" data-testid="dashboard-recent-canceled">
        <p className="bloq-headline text-lg text-[#005e7a]">Recently canceled</p>
        {data.recently_canceled?.length ? data.recently_canceled.slice(0, 6).map((b) => (
          <BookingRow key={b.booking_id} b={b} tz={userTz} muted onOpen={() => setSelected(b)} />
        )) : <p className="text-sm text-[#82828c]">No cancellations recently.</p>}
      </div>

      <BookingDrawer booking={selected} onClose={() => setSelected(null)} onChanged={reload} tz={userTz} />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, warning }) {
  return (
    <div className="bloq-card p-5 flex items-center justify-between" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#82828c]">{label}</p>
        <p className={`text-2xl font-semibold mt-1 ${warning ? "text-[#b45309]" : ""}`}>{value}</p>
      </div>
      <Icon className={`w-7 h-7 ${warning ? "text-[#b45309]" : "text-[#005e7a]"}`} />
    </div>
  );
}

function BookingRow({ b, tz, showApprove, muted, onOpen }) {
  const refresh = () => window.location.reload();
  const approve = async (e) => {
    e.stopPropagation();
    try { await api.post(`/booking/bookings/${b.booking_id}/approve`); toast.success("Approved"); refresh(); }
    catch (e2) { toast.error(formatApiError(e2)); }
  };
  const reject = async (e) => {
    e.stopPropagation();
    try { await api.post(`/booking/bookings/${b.booking_id}/reject`, { reason: "Rejected by host" }); toast.success("Rejected"); refresh(); }
    catch (e2) { toast.error(formatApiError(e2)); }
  };
  const clickable = !!onOpen;
  return (
    <div
      className={`flex items-center justify-between py-3 ${muted ? "opacity-70" : ""} ${clickable ? "cursor-pointer hover:bg-[#f5efe6] -mx-2 px-2 rounded-md" : ""}`}
      data-testid={`booking-row-${b.booking_id}`}
      onClick={clickable ? onOpen : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }
          : undefined
      }
    >
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{b.invitee_name} · {b.invitee_email}</p>
        <p className="text-xs text-[#82828c]">{formatInTz(b.start_time_utc, tz)} · {b.status}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {showApprove && (
          <>
            <button onClick={approve} className="bloq-btn-primary text-xs px-3 py-1.5" data-testid={`approve-${b.booking_id}`}><CheckCircle2 className="w-3 h-3 inline mr-1" /> Approve</button>
            <button onClick={reject} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#c43838]" data-testid={`reject-${b.booking_id}`}><XCircle className="w-3 h-3 inline mr-1" /> Reject</button>
          </>
        )}
      </div>
    </div>
  );
}

/* =====================================================================
 *  BOOKINGS LIST
 * ===================================================================== */
function BookingsListPanel({ workspace, user, initialBookingId, onBookingDrawerClose }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ status: "all", search: "" });
  // Sort order — keys mirror the backend sort_map on GET /booking/bookings.
  // Default to "when_desc" so the list opens to "soonest upcoming first"
  // exactly like before this dropdown existed.
  const [sort, setSort] = useState("created_desc");
  // Current / Archived tab — explicitly passed to the backend so the
  // list endpoint's default behavior (return everything) is unchanged
  // for any other caller. "current" hides soft-archived bookings;
  // "archived" shows only those.
  const [view, setView] = useState("current");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const bulk = useBulkSelect();
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const tz = user?.timezone || userBrowserTimezone();
  const load = async () => {
    if (!workspace) return;
    const params = { workspace_id: workspace.workspace_id, sort, archived: view };
    if (filters.status !== "all") params.status = filters.status;
    if (filters.search) params.search = filters.search;
    const r = await api.get(`/booking/bookings`, { params });
    setRows(r.data);
    bulk.clear();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace, filters.status, sort, view]);
  // Deep-link auto-open: when arriving with ?bookingId=X, fetch the
  // booking row directly so the drawer can open even if it isn't in the
  // current filtered list (e.g. user came from the dashboard).
  useEffect(() => {
    if (!initialBookingId || !workspace) return;
    let cancelled = false;
    api.get(`/booking/bookings/${initialBookingId}`)
      .then((r) => { if (!cancelled && r.data?.booking) setSelected(r.data.booking); })
      .catch(() => { /* silent — drawer just won't open */ });
    return () => { cancelled = true; };
  }, [initialBookingId, workspace]);
  const closeDrawer = () => {
    setSelected(null);
    if (onBookingDrawerClose) onBookingDrawerClose();
  };
  return (
    <div className="flex flex-col gap-4" data-testid="bookings-list">
      {/* Current / Archived tab strip — sits above the existing filter
          row so the search/sort/Add behave the same in either view. */}
      <div className="flex items-center gap-1 border-b border-[rgba(26,26,31,0.08)]">
        <button
          type="button"
          onClick={() => setView("current")}
          className={`text-xs px-4 py-2 border-b-2 -mb-px transition-colors ${
            view === "current"
              ? "border-[#005e7a] text-[#1a1a1f] font-medium"
              : "border-transparent text-[#82828c] hover:text-[#1a1a1f]"
          }`}
          data-testid="bookings-tab-current"
        >
          Current
        </button>
        <button
          type="button"
          onClick={() => setView("archived")}
          className={`text-xs px-4 py-2 border-b-2 -mb-px transition-colors ${
            view === "archived"
              ? "border-[#005e7a] text-[#1a1a1f] font-medium"
              : "border-transparent text-[#82828c] hover:text-[#1a1a1f]"
          }`}
          data-testid="bookings-tab-archived"
        >
          Archived
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          placeholder="Search name or email…"
          className="bloq-input flex-1 min-w-[200px]"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="bookings-search"
        />
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger className="w-44" data-testid="bookings-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="rescheduled">Rescheduled</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="no_show">No show</SelectItem>
          </SelectContent>
        </Select>
        {/* Sort dropdown — A-Z / Z-A variants for every column the
            bookings table renders (first name, last name, appointment
            type/title, status, payment, approval). When-asc / -desc
            sorts by start_time_utc. Backend `sort` query param maps
            1:1 with the keys below. */}
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-48" data-testid="bookings-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="created_desc">Newest first</SelectItem>
            <SelectItem value="created_asc">Oldest first</SelectItem>
            <SelectItem value="when_desc">When (latest first)</SelectItem>
            <SelectItem value="when_asc">When (earliest first)</SelectItem>
            <SelectItem value="first_asc">First name A–Z</SelectItem>
            <SelectItem value="first_desc">First name Z–A</SelectItem>
            <SelectItem value="last_asc">Last name A–Z</SelectItem>
            <SelectItem value="last_desc">Last name Z–A</SelectItem>
            <SelectItem value="title_asc">Appointment type A–Z</SelectItem>
            <SelectItem value="title_desc">Appointment type Z–A</SelectItem>
            <SelectItem value="status_asc">Status A–Z</SelectItem>
            <SelectItem value="status_desc">Status Z–A</SelectItem>
            <SelectItem value="payment_asc">Payment A–Z</SelectItem>
            <SelectItem value="payment_desc">Payment Z–A</SelectItem>
            <SelectItem value="approval_asc">Approval A–Z</SelectItem>
            <SelectItem value="approval_desc">Approval Z–A</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setShowCreate(true)}
          className="bloq-btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-2"
          data-testid="bookings-create-manual"
        >
          <Plus className="w-3.5 h-3.5" /> Add booking
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="bloq-card p-10 text-center">
          <CalendarClock className="w-10 h-10 mx-auto text-[#82828c] mb-3" />
          <p className="text-sm text-[#82828c]">No bookings match your filters.</p>
        </div>
      ) : (
        <>
          <BulkSelectToolbar
            selectedCount={bulk.count}
            totalVisible={rows.length}
            onSelectAll={() => bulk.selectAll(rows.map((b) => b.booking_id))}
            onClear={bulk.clear}
            onDelete={() => setBulkConfirm(true)}
            itemLabel="booking"
            busy={bulkBusy}
            testidPrefix="bookings-bulk"
            actionLabel={view === "archived" ? "Restore" : "Archive"}
            actionTone={view === "archived" ? "neutral" : "destructive"}
          />
          <div className="bloq-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#82828c]">
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-3 py-3">Invitee</th>
                  <th className="px-3 py-3">When</th>
                  <th className="px-3 py-3">Appointment type</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Payment</th>
                  <th className="px-3 py-3">Approval</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const checked = bulk.isSelected(b.booking_id);
                  return (
                    <tr
                      key={b.booking_id}
                      className={`hover:bg-[#f5efe6] cursor-pointer ${checked ? "bg-[#eef6f9]" : ""}`}
                      data-testid={`booking-${b.booking_id}`}
                      onClick={() => setSelected(b)}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); bulk.toggle(b.booking_id); }}
                          className="p-1 -m-1"
                          aria-label={checked ? "Unselect booking" : "Select booking"}
                          data-testid={`bookings-bulk-row-${b.booking_id}`}
                        >
                          {checked
                            ? <CheckSquare className="w-4 h-4 text-[#005e7a]" />
                            : <Square className="w-4 h-4 text-[#82828c]" />}
                        </button>
                      </td>
                      <td className="px-3 py-3"><span className="font-medium">{b.invitee_name}</span><br /><span className="text-xs text-[#82828c]">{b.invitee_email}</span></td>
                      <td className="px-3 py-3">{formatInTz(b.start_time_utc, tz)}</td>
                      <td className="px-3 py-3 text-[#4a4a52]">{b.title || "—"}</td>
                      <td className="px-3 py-3"><StatusPill status={b.status} /></td>
                      <td className="px-3 py-3"><span className="bloq-pill">{b.payment_status || "n/a"}</span></td>
                      <td className="px-3 py-3"><span className="bloq-pill">{b.approval_status || "n/a"}</span></td>
                      <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setSelected(b)} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid={`open-booking-${b.booking_id}`}>
                          Open <ChevronRight className="w-3 h-3 inline ml-1" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <BulkDeleteConfirmDialog
        open={bulkConfirm}
        onClose={() => setBulkConfirm(false)}
        onConfirm={async () => {
          setBulkBusy(true);
          try {
            // Archive vs restore depending on which tab the user is on.
            // Both endpoints take the same { workspace_id, ids } shape.
            const endpoint = view === "archived"
              ? `/booking/bookings/bulk-restore`
              : `/booking/bookings/bulk-delete`;
            const r = await api.post(endpoint, {
              workspace_id: workspace.workspace_id,
              ids: bulk.ids,
            });
            const n = view === "archived" ? r.data.restored : r.data.deleted;
            toast.success(`${view === "archived" ? "Restored" : "Archived"} ${n} booking(s)`);
            setBulkConfirm(false);
            load();
          } catch (e) {
            toast.error(formatApiError(e) || `Bulk ${view === "archived" ? "restore" : "archive"} failed`);
          } finally {
            setBulkBusy(false);
          }
        }}
        count={bulk.count}
        itemLabel="booking"
        description={
          view === "archived"
            ? "Restore the selected bookings to the Current tab. Invitee notifications, calendar holds, and payment history were preserved when they were archived."
            : "Bookings are archived (soft-deleted) — invitee notifications, calendar holds, and payment history stay in place. They'll move to the Archived tab and you can restore them anytime."
        }
        busy={bulkBusy}
        testidPrefix="bookings-bulk-delete"
        actionLabel={view === "archived" ? "Restore" : "Archive"}
        actionTone={view === "archived" ? "neutral" : "destructive"}
      />
      <BookingDrawer booking={selected} onClose={closeDrawer} onChanged={load} tz={tz} />
      <CreateBookingDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); load(); }}
        workspace={workspace}
        user={user}
      />
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    confirmed: { bg: "#005e7a", fg: "#fff" },
    pending: { bg: "#b45309", fg: "#fff" },
    rescheduled: { bg: "#1e3a8a", fg: "#fff" },
    canceled: { bg: "#c43838", fg: "#fff" },
    completed: { bg: "#15803d", fg: "#fff" },
    no_show: { bg: "#4a4a52", fg: "#fff" },
  };
  const c = colors[status] || { bg: "#f5efe6", fg: "#1a1a1f" };
  return <span className="bloq-pill" style={{ background: c.bg, color: c.fg }}>{status}</span>;
}

function BookingDrawer({ booking, onClose, onChanged, tz }) {
  const [detail, setDetail] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  // Shared reason field — visible at the bottom of the drawer body when
  // the booking is still in a state where Cancel or Decline make sense.
  // Declared here (above the early return) to keep hook order stable
  // across renders. Both Cancel and Decline read from this state.
  const [cancelReason, setCancelReason] = useState("");
  useEffect(() => {
    if (!booking) { setDetail(null); setEditMode(false); setRescheduleMode(false); setShowIntake(false); setCancelReason(""); return; }
    api.get(`/booking/bookings/${booking.booking_id}`).then((r) => setDetail(r.data));
  }, [booking]);
  if (!booking) return null;
  const b = detail?.booking || booking;
  const apt = detail?.appointment_type;
  const meeting = detail?.meeting;
  const responses = Array.isArray(b.form_responses) ? b.form_responses : [];
  const hasResponses = responses.length > 0;
  // The booking has a "join link" in three different shapes:
  //   - bloq_video / hybrid: in-app Bloq meeting at /m/<public_id>
  //   - external_link: the host pasted a Zoom/Meet/Teams URL on the location
  //   - everything else (in-person, phone, custom, tbd): no link
  // Gate by location_type so legacy bookings with a stale meeting_public_id
  // on in-person rows don't accidentally surface a Bloq meeting button.
  const ONLINE_BLOQ_TYPES = new Set(["bloq_video", "hybrid"]);
  const bloqMeetingUrl =
    ONLINE_BLOQ_TYPES.has(b.location_type) && (b.meeting_public_id || meeting?.public_id)
      ? `${window.location.origin}/m/${b.meeting_public_id || meeting.public_id}`
      : "";
  const externalUrl =
    b.location_type === "external_link" ? (b.external_join_url || "").trim() : "";
  const joinUrl = bloqMeetingUrl || externalUrl;
  const joinKind = bloqMeetingUrl ? "bloq" : externalUrl ? "external" : "";
  const isLive = !!joinUrl;
  const isTerminal = ["canceled", "completed", "no_show"].includes(b.status);
  const cancel = async () => {
    if (!window.confirm("Cancel this booking?")) return;
    try { await api.post(`/booking/bookings/${b.booking_id}/cancel`, { reason: cancelReason.trim() || "Canceled by host" }); toast.success("Canceled"); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const markComplete = async () => {
    try { await api.post(`/booking/bookings/${b.booking_id}/mark-complete`); toast.success("Marked complete"); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const markNoShow = async () => {
    try { await api.post(`/booking/bookings/${b.booking_id}/mark-no-show`); toast.success("Marked no-show"); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  // Approve / Decline a booking that's still pending host approval.
  // Surfaces the same endpoints the dashboard approval card uses, so
  // hosts don't have to leave the booking they're inspecting to act on
  // it. Only rendered when `b.approval_status === "pending"`.
  const approve = async () => {
    try { await api.post(`/booking/bookings/${b.booking_id}/approve`); toast.success("Approved"); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e) || "Approve failed"); }
  };
  const decline = async () => {
    if (!window.confirm("Decline this booking? The invitee will be notified.")) return;
    try { await api.post(`/booking/bookings/${b.booking_id}/reject`, { reason: cancelReason.trim() }); toast.success("Declined"); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e) || "Decline failed"); }
  };
  const copyJoin = async () => {
    if (!joinUrl) return;
    const ok = await copyText(joinUrl);
    if (ok) toast.success("Meeting link copied");
    else toast.error("Could not copy link");
  };
  const openMeeting = () => {
    if (!joinUrl) return;
    window.open(joinUrl, "_blank", "noopener");
  };
  if (editMode) {
    return <EditBookingDialog booking={b} onClose={() => setEditMode(false)} onSaved={() => { setEditMode(false); onChanged(); onClose(); }} />;
  }
  if (rescheduleMode) {
    return <RescheduleBookingDialog booking={b} onClose={() => setRescheduleMode(false)} onSaved={() => { setRescheduleMode(false); onChanged(); onClose(); }} tz={tz} />;
  }
  const isClosed = b.status === "canceled" || b.status === "completed";
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="booking-drawer">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="flex-1">{apt?.name || b.appointment_type_name || b.title || "Booking"} — {b.invitee_name}</DialogTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="w-8 h-8 inline-flex items-center justify-center hover:bg-[#f5efe6] rounded-md flex-shrink-0"
                  data-testid="booking-actions-menu"
                  aria-label="Booking actions"
                >
                  <MoreVertical className="w-4 h-4 text-[#4a4a52]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setEditMode(true)} data-testid="booking-menu-edit">
                  <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                </DropdownMenuItem>
                {!isClosed && (
                  <DropdownMenuItem onClick={() => setRescheduleMode(true)} data-testid="booking-menu-reschedule">
                    <CalendarClock className="w-3.5 h-3.5 mr-2" /> Reschedule
                  </DropdownMenuItem>
                )}
                {b.status === "confirmed" && (
                  <>
                    <DropdownMenuItem onClick={markComplete} data-testid="booking-menu-complete">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> Mark complete (end)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={markNoShow} data-testid="booking-menu-noshow">
                      <AlertCircle className="w-3.5 h-3.5 mr-2" /> Mark no-show
                    </DropdownMenuItem>
                  </>
                )}
                {!isClosed && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={cancel}
                      className="text-[#c43838] focus:text-[#c43838]"
                      data-testid="booking-menu-cancel"
                    >
                      <XCircle className="w-3.5 h-3.5 mr-2" /> Cancel booking
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-4 mt-2">
          {/* Pending-approval action strip — only renders when the
              booking is waiting on host approval. Reuses the existing
              backend endpoints the dashboard panel already calls; the
              drawer just lifts those actions next to the row so hosts
              don't have to bounce to the dashboard to act. */}
          {b.approval_status === "pending" && (
            <div
              className="rounded-md border border-[#e6c97a] bg-[#fff8e7] p-3 flex items-center justify-between gap-3"
              data-testid="booking-pending-approval-strip"
            >
              <div className="flex items-center gap-2 text-[#8a6d12] text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span><strong>Pending your approval.</strong> Approve to confirm and notify the invitee.</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={decline}
                  className="text-xs px-3 py-1.5 rounded bg-white border border-[#c43838]/40 text-[#c43838] hover:bg-[#c43838]/5 inline-flex items-center gap-1.5 font-medium"
                  data-testid="booking-decline-btn"
                >
                  <XCircle className="w-3.5 h-3.5" /> Decline
                </button>
                <button
                  type="button"
                  onClick={approve}
                  className="text-xs px-3 py-1.5 rounded bg-[#15803d] hover:bg-[#126931] text-white inline-flex items-center gap-1.5 font-medium"
                  data-testid="booking-approve-btn"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                </button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <DetailRow label="When" value={formatInTz(b.start_time_utc, tz)} />
            <DetailRow label="Status" value={<StatusPill status={b.status} />} />
            <DetailRow label="Email" value={b.invitee_email} />
            <DetailRow label="Phone" value={b.invitee_phone || "—"} />
            <DetailRow label="Invitee TZ" value={b.invitee_timezone} />
            <DetailRow label="Location" value={b.location_label || b.location_type || "—"} />
            {b.location_type === "bloq_video"
            ? (joinUrl && <DetailRow label="Address" value={joinUrl} />)
            : (b.location_address && <DetailRow label="Address" value={b.location_address} />)}
            <DetailRow label="Total" value={`${b.currency || "USD"} ${(b.total_amount || 0).toFixed(2)}`} />
          </div>
          {/* Meeting link — visible + clickable + copyable for any booking
              that carries one (bloq_video / hybrid / external_link). When
              the booking has no online component we hide the block
              entirely (in-person / phone / etc.). */}
          {isLive && (
            <div className="bloq-card p-3" data-testid="booking-join-link-card">
              <p className="text-xs uppercase tracking-widest text-[#82828c] font-medium mb-2">
                {joinKind === "bloq" ? "Bloq meeting link" : "External meeting link"}
              </p>
              <div className="flex items-center gap-2">
                <LinkIcon className="w-3.5 h-3.5 text-[#005e7a] flex-shrink-0" />
                <a
                  href={joinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-[#005e7a] underline truncate flex-1 min-w-0"
                  data-testid="booking-join-url"
                  title={joinUrl}
                >
                  {joinUrl}
                </a>
                <button
                  type="button"
                  onClick={copyJoin}
                  className="bloq-btn-secondary text-xs px-2 py-1.5 flex-shrink-0"
                  data-testid="booking-copy-join"
                  aria-label="Copy meeting link"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={openMeeting}
                  className="bloq-btn-primary text-xs px-3 py-1.5 flex-shrink-0 inline-flex items-center gap-1.5"
                  data-testid="booking-open-meeting"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Join
                </button>
              </div>
            </div>
          )}
          {b.notes && (
            <div>
              <p className="text-xs uppercase tracking-widest text-[#82828c]">Notes</p>
              <p className="text-sm mt-1 bg-[#f5efe6] p-3 rounded-md whitespace-pre-line">{b.notes}</p>
            </div>
          )}
          {/* Intake form — show a dedicated button so admins can always find
              this content. Opens a focused modal with all responses. The
              previous inline strip was easy to miss when the booking had a
              long Notes field above it. */}
          <div
            className="flex items-center justify-between bloq-card p-3"
            data-testid="booking-intake-row"
          >
            <div>
              <p className="text-sm font-medium text-[#1a1a1f]">Intake form responses</p>
              <p className="text-xs text-[#82828c]">
                {hasResponses
                  ? `${responses.length} response${responses.length === 1 ? "" : "s"} submitted by ${b.invitee_name}`
                  : "No intake form was attached to this booking, or the invitee didn't submit one."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowIntake(true)}
              disabled={!hasResponses}
              className={`text-xs px-3 py-1.5 ${hasResponses ? "bloq-btn-secondary" : "bloq-btn-secondary opacity-50 cursor-not-allowed"}`}
              data-testid="booking-view-intake-btn"
            >
              <FileText className="w-3 h-3 inline mr-1" /> View intake form
            </button>
          </div>
          {/* Cancellation / decline reason — shared field used by both
              Cancel (action menu) and Decline (pending-approval strip).
              Reuses the same `bloq-input` styling as the public cancel
              page and the block-host-time dialog. Hidden once the
              booking has already reached a terminal state. */}
          {!isTerminal && (
            <div data-testid="booking-cancel-reason-row">
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">
                Cancellation / decline reason (optional)
              </label>
              <textarea
                rows={2}
                className="bloq-input text-sm"
                placeholder="Sent to the invitee on cancel or decline."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                data-testid="booking-cancel-reason"
              />
            </div>
          )}
        </div>
        <IntakeResponseModal
          open={showIntake}
          onClose={() => setShowIntake(false)}
          booking={b}
          responses={responses}
        />
      </DialogContent>
    </Dialog>
  );
}

function IntakeResponseModal({ open, onClose, booking, responses }) {
  // Renders the captured intake-form responses verbatim, including object
  // values (e.g. address subfields, price+currency) decoded into readable
  // key/value pairs. Files (`file` field type) show up as a link.
  const renderValue = (v) => {
    if (v === null || v === undefined || v === "") return <span className="text-[#82828c] italic">(empty)</span>;
    if (typeof v === "string") return <span className="whitespace-pre-line">{v}</span>;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (Array.isArray(v)) {
      if (!v.length) return <span className="text-[#82828c] italic">(empty)</span>;
      return v.join(", ");
    }
    if (typeof v === "object") {
      // File response shape: { file_id, filename, url }
      if (v.url && v.filename) {
        return (
          <a
            href={v.url}
            target="_blank"
            rel="noreferrer"
            className="text-[#005e7a] underline"
          >
            {v.filename}
          </a>
        );
      }
      // Address object shape — render the composed address if present
      if (v.line1 || v.city || v.country) {
        const composed = [v.line1, v.line2, v.city, v.state, v.postal_code, v.country].filter(Boolean).join(", ");
        return composed || JSON.stringify(v);
      }
      // Price/currency shape
      if (v.amount !== undefined && v.currency) {
        return `${v.currency} ${v.amount}`;
      }
      return <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(v, null, 2)}</pre>;
    }
    return String(v);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="intake-response-modal">
        <DialogHeader>
          <DialogTitle>Intake responses</DialogTitle>
          <DialogDescription>
            Submitted by {booking.invitee_name} ({booking.invitee_email}).
          </DialogDescription>
        </DialogHeader>
        {responses.length === 0 ? (
          <p className="text-sm text-[#82828c]">No responses recorded.</p>
        ) : (
          <div className="flex flex-col divide-y divide-[#e5e0d6]">
            {responses.map((r, i) => (
              <div key={r.field_id || i} className="py-3" data-testid={`intake-response-${i}`}>
                <p className="text-[10px] uppercase tracking-widest text-[#82828c]">{r.label || "Question"}</p>
                <div className="text-sm text-[#1a1a1f] mt-1 break-words">{renderValue(r.value)}</div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="bloq-btn-secondary" data-testid="intake-response-close">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateBookingDialog({ open, onClose, onCreated, workspace, user }) {
  const tz = user?.timezone || userBrowserTimezone();
  const [form, setForm] = useState({
    appointment_type_id: "",      // empty string = ad-hoc/custom booking
    appointment_location_id: "",  // chosen AT-location record
    title: "", description: "",
    invitee_first_name: "", invitee_last_name: "",
    invitee_email: "", invitee_phone: "", invitee_timezone: tz,
    start_date: "", start_time: "", duration_minutes: 30,
    location_type: "bloq_video", location_address: "", notes: "",
    // Ad-hoc-only metadata mirrors the create-AT modal so admins typing
    // a new appointment-type name on the fly can still capture pricing
    // / category / classification. Stored alongside the booking doc
    // (the backend accepts arbitrary scalar fields).
    category_id: "", classification: "",
    price_amount: 0, price_currency: "USD", sale_price_amount: 0,
  });
  // List of existing ATs in the workspace + per-AT location list +
  // every workspace-level location (so when the admin picks "Custom"
  // they can still choose from the full locations catalog they manage
  // under the appointment-types panel).
  const [apts, setApts] = useState([]);
  const [aptLocations, setAptLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  // Load ATs + categories + the full locations catalog when the dialog
  // opens. The locations catalog drives the Custom-mode dropdown.
  useEffect(() => {
    if (!open || !workspace?.workspace_id) return;
    (async () => {
      try {
        const [aRes, cRes, lRes] = await Promise.all([
          api.get(`/booking/appointment-types`, { params: { workspace_id: workspace.workspace_id, status: "active" } }),
          api.get(`/booking/categories`, { params: { workspace_id: workspace.workspace_id } }).catch(() => ({ data: [] })),
          api.get(`/booking/locations`, { params: { workspace_id: workspace.workspace_id } }).catch(() => ({ data: [] })),
        ]);
        setApts(aRes.data || []);
        setCategories(cRes.data || []);
        setAllLocations(lRes.data || []);
      } catch { /* non-fatal — modal still works as ad-hoc */ }
    })();
  }, [open, workspace?.workspace_id]);

  // Whenever the AT selection changes, refresh the AT-specific location
  // dropdown + auto-fill duration / location_type so the form mirrors
  // what visitors see on the public booking flow. Falls back to a
  // free-text location row when the admin picks "Custom / no AT".
  useEffect(() => {
    if (!open) return;
    const aptId = form.appointment_type_id;
    if (!aptId) { setAptLocations([]); return; }
    (async () => {
      try {
        const r = await api.get(`/booking/appointment-types/${aptId}`);
        const apt = r.data || {};
        const locIds = apt.location_ids || [];
        let locs = [];
        if (locIds.length) {
          const lr = await api.get(`/booking/locations`, { params: { workspace_id: workspace.workspace_id } }).catch(() => ({ data: [] }));
          const byId = new Map((lr.data || []).map((l) => [l.location_id, l]));
          locs = locIds.map((id) => byId.get(id)).filter(Boolean);
        }
        setAptLocations(locs);
        setForm((f) => ({
          ...f,
          // Copy the AT's display name to title so the booking carries
          // the same label visitors would see on the booking link.
          title: apt.name || f.title,
          description: apt.description || f.description,
          duration_minutes: apt.duration_minutes || f.duration_minutes,
          price_amount: apt.price_amount ?? f.price_amount,
          price_currency: apt.price_currency || f.price_currency,
          sale_price_amount: apt.sale_price_amount ?? f.sale_price_amount,
          category_id: apt.category_id || f.category_id,
          classification: apt.classification || f.classification,
          // Auto-pick the AT's default location, mirroring public flow.
          appointment_location_id: apt.default_location_id || (locs[0]?.location_id) || "",
        }));
      } catch { setAptLocations([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.appointment_type_id, open]);

  const submit = async () => {
    if (!form.invitee_first_name.trim()) { toast.error("First name required"); return; }
    if (!form.invitee_email.trim()) { toast.error("Email required"); return; }
    if (!form.start_date || !form.start_time) { toast.error("Pick a date and time"); return; }
    const local = new Date(`${form.start_date}T${form.start_time}`);
    if (Number.isNaN(local.getTime())) { toast.error("Invalid date/time"); return; }
    const startIso = local.toISOString();
    setSaving(true);
    try {
      await api.post(`/booking/bookings`, {
        workspace_id: workspace.workspace_id,
        appointment_type_id: form.appointment_type_id || undefined,
        appointment_location_id: form.appointment_location_id || undefined,
        title: form.title || "Manual booking",
        invitee_first_name: form.invitee_first_name,
        invitee_last_name: form.invitee_last_name,
        invitee_email: form.invitee_email,
        invitee_phone: form.invitee_phone,
        invitee_timezone: form.invitee_timezone,
        start_time_utc: startIso,
        duration_minutes: Number(form.duration_minutes) || 30,
        // Send a location_type fallback only when no AT-location is
        // chosen — the backend will derive it from the location record
        // when appointment_location_id is set.
        location_type: form.appointment_location_id ? undefined : form.location_type,
        location_address: form.location_address,
        notes: form.notes || "",
        description: form.description || "",
        category_id: form.category_id || undefined,
        // Forward the price fields the form already collects so the
        // backend can persist them (and derive payment_status).
        price_amount: Number(form.price_amount) || 0,
        sale_price_amount: Number(form.sale_price_amount) || 0,
        price_currency: form.price_currency || "USD",
        source: "manual",
      });
      toast.success("Booking added");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-booking-dialog">
        <DialogHeader><DialogTitle>Add booking</DialogTitle></DialogHeader>
        <p className="text-xs text-[#82828c] -mt-2 mb-3">Manually create a booking for an invitee without a public link. Confirmation emails fire automatically.</p>

        <div className="grid sm:grid-cols-2 gap-3">
          {/* Appointment-type picker — drives the location dropdown
              below and auto-fills duration / price metadata. Leaving it
              blank ("Custom / no appointment type") exposes the ad-hoc
              metadata fields so admins can capture a one-off booking
              without first creating an AT. Kept INSIDE the form grid
              so its `col-span-2` only affects this nested grid; placing
              it directly inside DialogContent (also `display: grid`)
              would silently spawn a second column at the dialog level
              and the footer buttons would wrap next to the form. */}
          <Field label="Appointment type" full>
            <Select
              value={form.appointment_type_id || "__none__"}
              onValueChange={(v) => setForm({ ...form, appointment_type_id: v === "__none__" ? "" : v, appointment_location_id: "" })}
            >
              <SelectTrigger data-testid="cb-appointment-type"><SelectValue placeholder="Pick an appointment type…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Custom / no appointment type</SelectItem>
                {apts.map((a) => (
                  <SelectItem key={a.appointment_type_id} value={a.appointment_type_id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Contact fields — surfaced at the top so the host can identify
              who they're booking for before nailing down the appointment
              details below. Order matches the EDIT modal so the two flows
              feel identical. */}
          <Field label="Invitee first name *">
            <ContactAutocomplete
              workspaceId={workspace?.workspace_id}
              value={form.invitee_first_name}
              onChange={(v) => setForm({ ...form, invitee_first_name: v })}
              onSelect={(c) => setForm((f) => ({
                ...f,
                invitee_first_name: c.first_name || (c.name || "").split(" ")[0] || f.invitee_first_name,
                invitee_last_name: f.invitee_last_name || c.last_name || (c.name || "").split(" ").slice(1).join(" ") || "",
                invitee_email: f.invitee_email || c.email || "",
                invitee_phone: f.invitee_phone || c.phone || "",
              }))}
              placeholder="Search contacts or type a new first name…"
              testid="cb-firstname"
            />
          </Field>
          <Field label="Last name"><input className="bloq-input" value={form.invitee_last_name} onChange={(e) => setForm({ ...form, invitee_last_name: e.target.value })} data-testid="cb-lastname" /></Field>
          <Field label="Email *">
            <ContactAutocomplete
              type="email"
              workspaceId={workspace?.workspace_id}
              value={form.invitee_email}
              onChange={(v) => setForm({ ...form, invitee_email: v })}
              onSelect={(c) => setForm((f) => ({
                ...f,
                invitee_email: c.email || f.invitee_email,
                invitee_first_name: f.invitee_first_name || c.first_name || (c.name || "").split(" ")[0] || "",
                invitee_last_name: f.invitee_last_name || c.last_name || (c.name || "").split(" ").slice(1).join(" ") || "",
                invitee_phone: f.invitee_phone || c.phone || "",
              }))}
              placeholder="Search contacts or type a new email…"
              testid="cb-email"
            />
          </Field>
          <Field label="Phone"><input className="bloq-input" value={form.invitee_phone} onChange={(e) => setForm({ ...form, invitee_phone: e.target.value })} /></Field>

          <Field label="Title" full>
            <input className="bloq-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Discovery call" data-testid="cb-title" />
          </Field>
          {/* Ad-hoc metadata block — only renders when no AT is selected
              so the admin can capture description / duration / category
              / pricing the same way the AT-create modal does. When an
              AT IS selected these values are inherited and read-only. */}
          {!form.appointment_type_id && (
            <>
              <Field label="Description" full>
                <textarea className="bloq-input min-h-[72px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cb-description" />
              </Field>
              <Field label="Category">
                <Select value={form.category_id || "__none__"} onValueChange={(v) => setForm({ ...form, category_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="cb-category"><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Uncategorized</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Classification">
                <Select value={form.classification || "individual"} onValueChange={(v) => setForm({ ...form, classification: v })}>
                  <SelectTrigger data-testid="cb-classification"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="group">Group</SelectItem>
                    <SelectItem value="class">Class</SelectItem>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="webinar">Webinar</SelectItem>
                    <SelectItem value="consultation">Consultation</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Price">
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <input type="number" step="0.01" className="bloq-input w-full" value={form.price_amount} onChange={(e) => setForm({ ...form, price_amount: Number(e.target.value) })} data-testid="cb-price" />
                  </div>
                  <div className="w-24">
                    <label className="text-[10px] uppercase tracking-widest text-[#82828c] block mb-1">Currency</label>
                    <input className="bloq-input w-full" value={form.price_currency} onChange={(e) => setForm({ ...form, price_currency: e.target.value.toUpperCase() })} maxLength={3} placeholder="USD" data-testid="cb-price-currency" />
                  </div>
                </div>
              </Field>
              <Field label="Sale price">
                <input type="number" step="0.01" className="bloq-input" value={form.sale_price_amount} onChange={(e) => setForm({ ...form, sale_price_amount: Number(e.target.value) })} data-testid="cb-sale-price" />
              </Field>
            </>
          )}
          <Field label="Date *"><input type="date" className="bloq-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="cb-date" /></Field>
          <Field label="Time *"><input type="time" className="bloq-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="cb-time" /></Field>
          <Field label="Duration (min)"><input type="number" min={5} max={480} className="bloq-input" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></Field>
          <Field label="Invitee timezone">
            <Select value={form.invitee_timezone} onValueChange={(v) => setForm({ ...form, invitee_timezone: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Location">
            {/* When an AT is selected, narrow the dropdown to that AT's
                allowed locations (matches public booking-flow gating).
                When the AT is "Custom / no appointment type", expose
                the FULL workspace locations catalog so the admin picks
                from the same locations they manage under appointment
                types — never a stale enum of generic types. */}
            {form.appointment_type_id ? (
              aptLocations.length > 0 ? (
                <Select
                  value={form.appointment_location_id || ""}
                  onValueChange={(v) => setForm({ ...form, appointment_location_id: v })}
                >
                  <SelectTrigger data-testid="cb-appt-location"><SelectValue placeholder="Pick a location…" /></SelectTrigger>
                  <SelectContent>
                    {aptLocations.map((l) => (
                      <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-[#82828c]">This appointment type has no locations attached. Add one under <strong>Appointment types → Locations</strong>.</p>
              )
            ) : (
              <Select
                value={form.appointment_location_id || ""}
                onValueChange={(v) => setForm({ ...form, appointment_location_id: v })}
              >
                <SelectTrigger data-testid="cb-custom-location"><SelectValue placeholder="Pick a location…" /></SelectTrigger>
                <SelectContent>
                  {allLocations.length === 0 ? (
                    <div className="text-xs text-[#82828c] px-3 py-2">No locations yet — add some under Appointment types → Locations.</div>
                  ) : allLocations.map((l) => (
                    <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Notes" full>
            <textarea rows={2} className="bloq-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="bloq-btn-secondary">Cancel</button>
          <button onClick={submit} disabled={saving} className="bloq-btn-primary" data-testid="cb-submit">{saving ? "Adding…" : "Add booking"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditBookingDialog({ booking, onClose, onSaved }) {
  const b = booking;
  const initLocalDateTime = (iso) => {
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return {
        date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      };
    } catch { return { date: "", time: "" }; }
  };
  const startLocal = initLocalDateTime(b.start_time_utc);
  const endLocal = initLocalDateTime(b.end_time_utc);

  // Pull the appointment type, its allowed locations, and categories so the
  // admin edit experience mirrors what the customer saw at booking time
  // (per-customer-request: "the location field should pull from the
  // locations of the appointment type, and the categories should pull
  // from categories").
  const [aptType, setAptType] = useState(null);
  const [allowedLocations, setAllowedLocations] = useState([]); // [{location_id, name, type, address_formatted}]
  const [categories, setCategories] = useState([]); // [{category_id, name, color}]
  const [loadingMeta, setLoadingMeta] = useState(true);
  // List of ATs in this workspace so the admin can SWAP the booking's
  // appointment type. Re-runs `loadMeta` when the picked AT changes so
  // the location dropdown follows.
  const [allApts, setAllApts] = useState([]);
  // Toggle controls whether saving fires a fresh confirmation email
  // to the invitee. Defaults OFF — a silent admin edit shouldn't spam
  // the visitor unless the host opts in.
  const [resendConfirmation, setResendConfirmation] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadMeta() {
      try {
        const tasks = [];
        // Use the form's CURRENT appointment_type_id so swapping the
        // picker triggers a re-load of allowed locations + AT details.
        const pickedAptId = (form && form.appointment_type_id) || b.appointment_type_id;
        if (pickedAptId) {
          tasks.push(api.get(`/booking/appointment-types/${pickedAptId}`).then((r) => r.data).catch(() => null));
        } else {
          tasks.push(Promise.resolve(null));
        }
        tasks.push(api.get(`/booking/categories`, { params: { workspace_id: b.workspace_id } }).then((r) => r.data || []).catch(() => []));
        tasks.push(api.get(`/booking/locations`, { params: { workspace_id: b.workspace_id } }).then((r) => r.data || []).catch(() => []));
        // Pull the full AT list once — used to drive the swap dropdown.
        tasks.push(api.get(`/booking/appointment-types`, { params: { workspace_id: b.workspace_id, status: "active" } }).then((r) => r.data || []).catch(() => []));
        const [apt, cats, allLocs, aptList] = await Promise.all(tasks);
        if (cancelled) return;
        setAptType(apt);
        setCategories(cats || []);
        setAllApts(aptList || []);
        if (apt && Array.isArray(apt.location_ids) && apt.location_ids.length) {
          const wanted = new Set(apt.location_ids);
          setAllowedLocations((allLocs || []).filter((l) => wanted.has(l.location_id)));
        } else {
          // No constraint on this booking's appt type — fall back to all
          // workspace locations so the admin can still pick from a real list.
          setAllowedLocations(allLocs || []);
        }
      } catch {
        // Soft-fail to legacy free-text mode
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    }
    loadMeta();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.appointment_type_id, b.workspace_id]);

  const [form, setForm] = useState({
    appointment_type_id: b.appointment_type_id || "",
    invitee_first_name: b.invitee_first_name || (b.invitee_name || "").split(" ")[0] || "",
    invitee_last_name: b.invitee_last_name || (b.invitee_name || "").split(" ").slice(1).join(" ") || "",
    invitee_email: b.invitee_email || "",
    invitee_phone: b.invitee_phone || "",
    invitee_timezone: b.invitee_timezone || "UTC",
    start_date: startLocal.date, start_time: startLocal.time,
    end_date: endLocal.date, end_time: endLocal.time,
    appointment_location_id: b.appointment_location_id || "",
    location_type: b.location_type || "bloq_video",
    location_address: b.location_address || "",
    category_id: b.category_id || "",
    classification: b.classification || "",
    notes: b.notes || "",
    title: b.title || "",
    description: b.description || "",
    duration_minutes: b.duration_minutes || 30,
    price_amount: b.price_amount ?? 0,
    price_currency: b.price_currency || "USD",
    sale_price_amount: b.sale_price_amount ?? 0,
  });

  // Centralized AT-change handler — used by both the picker dropdown
  // and any future programmatic AT swap. Picking a real AT auto-fills
  // its metadata into the form (so the modal reflects the AT's current
  // settings). Picking "Custom / no appointment type" BLANKS all the
  // AT-derived fields (title, description, duration, price, currency,
  // sale price, category, classification, location) — invitee contact
  // info is intentionally preserved because it has nothing to do with
  // the appointment type.
  const onPickApt = (nextId) => {
    if (!nextId) {
      // Custom mode → wipe AT-derived metadata.
      setForm((f) => ({
        ...f,
        appointment_type_id: "",
        title: "",
        description: "",
        duration_minutes: 30,
        price_amount: 0,
        price_currency: "USD",
        sale_price_amount: 0,
        category_id: "",
        classification: "",
        appointment_location_id: "",
        location_type: "bloq_video",
        location_address: "",
      }));
      setAptType(null);
      // Reset the location dropdown to the FULL workspace catalog so
      // the admin can pick any location, not just the ones the
      // previously-linked AT happened to allow. The picker is keyed on
      // allowedLocations — without this it stays scoped to the old AT.
      (async () => {
        try {
          const r = await api.get(`/booking/locations`, { params: { workspace_id: b.workspace_id } });
          setAllowedLocations(r.data || []);
        } catch { /* non-fatal */ }
      })();
      return;
    }
    setForm((f) => ({ ...f, appointment_type_id: nextId, appointment_location_id: "" }));
  };

  // When the AT picker changes, re-fetch the AT + its allowed locations.
  // We pipe through the same loadMeta flow used at mount.
  useEffect(() => {
    if (!form.appointment_type_id || form.appointment_type_id === b.appointment_type_id) return;
    let cancelled = false;
    (async () => {
      try {
        const apt = await api.get(`/booking/appointment-types/${form.appointment_type_id}`).then((r) => r.data).catch(() => null);
        const allLocs = await api.get(`/booking/locations`, { params: { workspace_id: b.workspace_id } }).then((r) => r.data || []).catch(() => []);
        if (cancelled) return;
        setAptType(apt);
        if (apt && Array.isArray(apt.location_ids) && apt.location_ids.length) {
          const wanted = new Set(apt.location_ids);
          setAllowedLocations(allLocs.filter((l) => wanted.has(l.location_id)));
        } else {
          setAllowedLocations(allLocs);
        }
        // Mirror CREATE behaviour — fill the metadata fields from the
        // newly chosen AT so the modal reflects the AT's current
        // settings. Invitee contact info is preserved.
        if (apt) {
          setForm((f) => ({
            ...f,
            appointment_location_id: apt.default_location_id || "",
            title: apt.name || f.title,
            description: apt.description || "",
            duration_minutes: apt.duration_minutes || f.duration_minutes,
            price_amount: apt.price_amount ?? 0,
            price_currency: apt.price_currency || "USD",
            sale_price_amount: apt.sale_price_amount ?? 0,
            category_id: apt.category_id || "",
            classification: apt.classification || "",
          }));
        }
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.appointment_type_id]);

  // When the appt type loads, set sensible defaults that don't overwrite
  // a value the user has already edited mid-flight.
  useEffect(() => {
    if (!aptType) return;
    setForm((f) => ({
      ...f,
      // If the booking has a saved location_id and it's still in the
      // allowed set, keep it; otherwise default to the apt's preferred one.
      appointment_location_id:
        f.appointment_location_id && allowedLocations.some((l) => l.location_id === f.appointment_location_id)
          ? f.appointment_location_id
          : (aptType.default_location_id || (allowedLocations[0] && allowedLocations[0].location_id) || ""),
      category_id: f.category_id || aptType.category_id || "",
    }));
  }, [aptType, allowedLocations]);

  // Mirror the picked location into the legacy free-text fields so the
  // confirmation email + ICS still render the right label/address even if
  // the chosen appointment_location_id doesn't get fully wired through
  // every legacy code path.
  const onPickLocation = (id) => {
    const loc = allowedLocations.find((l) => l.location_id === id);
    if (!loc) {
      setForm((f) => ({ ...f, appointment_location_id: id }));
      return;
    }
    const LOC_TYPE_MAP = {
      in_person: "in_person", hybrid: "hybrid", phone: "phone",
      online: "bloq_video", custom: "custom",
    };
    setForm((f) => ({
      ...f,
      appointment_location_id: id,
      location_type: LOC_TYPE_MAP[loc.type] || "custom",
      location_address: loc.address_formatted || loc.address || "",
    }));
  };

  const [saving, setSaving] = useState(false);
  const save = async () => {
    const start = new Date(`${form.start_date}T${form.start_time}`);
    const end = new Date(`${form.end_date}T${form.end_time}`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) { toast.error("Invalid date/time"); return; }
    if (end <= start) { toast.error("End must be after start"); return; }
    setSaving(true);
    try {
      await api.patch(`/booking/bookings/${b.booking_id}`, {
        appointment_type_id: form.appointment_type_id || null,
        title: form.title || undefined,
        description: form.description || "",
        invitee_first_name: form.invitee_first_name,
        invitee_last_name: form.invitee_last_name,
        invitee_name: `${form.invitee_first_name} ${form.invitee_last_name}`.trim(),
        invitee_email: form.invitee_email,
        invitee_phone: form.invitee_phone,
        invitee_timezone: form.invitee_timezone,
        start_time_utc: start.toISOString(),
        end_time_utc: end.toISOString(),
        appointment_location_id: form.appointment_location_id || null,
        location_type: form.location_type,
        location_address: form.location_address,
        category_id: form.category_id || null,
        classification: form.classification || null,
        price_amount: form.price_amount,
        price_currency: form.price_currency,
        sale_price_amount: form.sale_price_amount,
        notes: form.notes,
        // Flip the resend flag — backend re-enqueues the confirmation
        // email template with the latest booking state when true.
        resend_confirmation: resendConfirmation,
      });
      toast.success(resendConfirmation ? "Booking updated · confirmation re-sent" : "Booking updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-booking-dialog">
        <DialogHeader><DialogTitle>Edit booking</DialogTitle></DialogHeader>
        <p className="text-xs text-[#82828c] -mt-2 mb-3">
          Admin override — all booking-link limits are bypassed. Location and category options
          are pulled from <strong>{aptType?.name || "this booking's appointment type"}</strong>.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          {/* Appointment-type picker — kept INSIDE this nested grid so
              its `col-span-2` is scoped to the form layout. Hoisting it
              up to be a direct child of DialogContent (which itself is
              `display: grid`) caused the parent grid to auto-create a
              second column and the footer buttons wrapped next to the
              form content. */}
          <Field label="Appointment type" full>
            <Select
              value={form.appointment_type_id || "__none__"}
              onValueChange={(v) => onPickApt(v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="eb-appointment-type"><SelectValue placeholder="Pick an appointment type…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Custom / no appointment type</SelectItem>
                {allApts.map((a) => (
                  <SelectItem key={a.appointment_type_id} value={a.appointment_type_id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Contact fields — surfaced at the top of the form so the
              host can see who they're editing for before tweaking the
              appointment details. Order matches the CREATE modal. */}
          <Field label="First name"><input className="bloq-input" value={form.invitee_first_name} onChange={(e) => setForm({ ...form, invitee_first_name: e.target.value })} /></Field>
          <Field label="Last name"><input className="bloq-input" value={form.invitee_last_name} onChange={(e) => setForm({ ...form, invitee_last_name: e.target.value })} /></Field>
          <Field label="Email">
            <ContactAutocomplete
              type="email"
              workspaceId={b.workspace_id}
              value={form.invitee_email}
              onChange={(v) => setForm({ ...form, invitee_email: v })}
              onSelect={(c) => setForm((f) => ({
                ...f,
                invitee_email: c.email || f.invitee_email,
                invitee_first_name: f.invitee_first_name || c.first_name || (c.name || "").split(" ")[0] || "",
                invitee_last_name: f.invitee_last_name || c.last_name || (c.name || "").split(" ").slice(1).join(" ") || "",
                invitee_phone: f.invitee_phone || c.phone || "",
              }))}
              placeholder="Search contacts or type a new email…"
              testid="eb-email"
            />
          </Field>
          <Field label="Phone"><input className="bloq-input" value={form.invitee_phone} onChange={(e) => setForm({ ...form, invitee_phone: e.target.value })} /></Field>

          {/* Title — when no AT is linked, this is the visible label
              for the booking everywhere it surfaces (calendar invite
              summary, list views, etc). When an AT IS linked we let
              the admin override the AT name on a per-booking basis. */}
          <Field label="Title / appointment name" full>
            <input className="bloq-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={aptType?.name || "e.g. Discovery call"} data-testid="eb-title" />
          </Field>
          {/* Ad-hoc metadata block — only renders when no AT is linked,
              mirrors the CREATE modal so the two paths capture the
              same data shape (description, category, classification,
              price, currency, sale price). When an AT IS linked,
              these values are inherited from it and the block is
              hidden — admins can still tweak the AT itself from the
              Appointment-types tab. */}
          {!form.appointment_type_id && (
            <>
              <Field label="Description" full>
                <textarea rows={2} className="bloq-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short context about this booking" data-testid="eb-description" />
              </Field>
              <Field label="Category">
                <Select value={form.category_id || "__none__"} onValueChange={(v) => setForm({ ...form, category_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="eb-category"><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Uncategorized</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Classification">
                <Select value={form.classification || "individual"} onValueChange={(v) => setForm({ ...form, classification: v })}>
                  <SelectTrigger data-testid="eb-classification"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="group">Group</SelectItem>
                    <SelectItem value="class">Class</SelectItem>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="webinar">Webinar</SelectItem>
                    <SelectItem value="consultation">Consultation</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Price">
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <input type="number" step="0.01" className="bloq-input w-full" value={form.price_amount} onChange={(e) => setForm({ ...form, price_amount: Number(e.target.value) })} data-testid="eb-price" />
                  </div>
                  <div className="w-24">
                    <label className="text-[10px] uppercase tracking-widest text-[#82828c] block mb-1">Currency</label>
                    <input className="bloq-input w-full" value={form.price_currency} onChange={(e) => setForm({ ...form, price_currency: e.target.value.toUpperCase() })} maxLength={3} placeholder="USD" data-testid="eb-price-currency" />
                  </div>
                </div>
              </Field>
              <Field label="Sale price">
                <input type="number" step="0.01" className="bloq-input" value={form.sale_price_amount} onChange={(e) => setForm({ ...form, sale_price_amount: Number(e.target.value) })} data-testid="eb-sale-price" />
              </Field>
            </>
          )}
          <Field label="Start date"><input type="date" className="bloq-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
          <Field label="Start time"><input type="time" className="bloq-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></Field>
          <Field label="End date"><input type="date" className="bloq-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          <Field label="End time"><input type="time" className="bloq-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></Field>
          <Field label="Invitee timezone">
            <Select value={form.invitee_timezone} onValueChange={(v) => setForm({ ...form, invitee_timezone: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {/* Location — pulls from the workspace locations catalog
              (the same one managed under Appointment types → Locations).
              When this booking is tied to an AT, allowedLocations is
              narrowed to that AT's allowed list. When the AT is set to
              Custom, allowedLocations holds every workspace location
              so the admin can still pick from the full catalog. */}
          <Field label="Location">
            {!loadingMeta && allowedLocations.length > 0 ? (
              <Select
                value={form.appointment_location_id || "__none__"}
                onValueChange={(v) => onPickLocation(v === "__none__" ? "" : v)}
              >
                <SelectTrigger data-testid="eb-appt-location"><SelectValue placeholder="Pick a location" /></SelectTrigger>
                <SelectContent>
                  {allowedLocations.map((l) => (
                    <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-[#82828c]">No locations in this workspace yet. Add one under <strong>Appointment types → Locations</strong>.</p>
            )}
          </Field>
          {/* Category — only render the picker when the workspace has any. */}
          {categories.length > 0 && (
            <Field label="Category">
              <Select
                value={form.category_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, category_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="eb-category"><SelectValue placeholder="No category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No category</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Notes" full><textarea rows={2} className="bloq-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
        <DialogFooter className="flex flex-wrap items-center gap-2">
          {/* Opt-in to resend the confirmation email after saving.
              Defaults OFF so a silent metadata edit doesn't spam the
              invitee — the host has to deliberately tick the box. */}
          <label className="flex items-center gap-2 mr-auto text-xs text-[#4a4a52] cursor-pointer" data-testid="eb-resend-wrap">
            <input
              type="checkbox"
              className="w-3.5 h-3.5 accent-[#005e7a] cursor-pointer"
              checked={resendConfirmation}
              onChange={(e) => setResendConfirmation(e.target.checked)}
              data-testid="eb-resend-toggle"
            />
            Resend booking confirmation
          </label>
          <button onClick={onClose} className="bloq-btn-secondary">Cancel</button>
          <button onClick={save} disabled={saving} className="bloq-btn-primary" data-testid="eb-save">{saving ? "Saving…" : "Save changes"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RescheduleBookingDialog({ booking, onClose, onSaved, tz }) {
  const b = booking;
  const initLocal = (iso) => {
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return {
        date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      };
    } catch { return { date: "", time: "" }; }
  };
  const init = initLocal(b.start_time_utc);
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const local = new Date(`${form.date}T${form.time}`);
    if (Number.isNaN(local.getTime())) { toast.error("Invalid date/time"); return; }
    setSaving(true);
    try {
      await api.post(`/booking/bookings/${b.booking_id}/admin-reschedule`, { start_time_utc: local.toISOString() });
      toast.success("Rescheduled");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reschedule-booking-dialog">
        <DialogHeader><DialogTitle>Reschedule booking</DialogTitle></DialogHeader>
        <p className="text-xs text-[#82828c] -mt-2 mb-3">Admin override — notice and limit checks are skipped. Both parties are emailed automatically.</p>
        <div className="text-xs text-[#82828c] mb-3">Currently: {formatInTz(b.start_time_utc, tz)}</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="New date"><input type="date" className="bloq-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="New time"><input type="time" className="bloq-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="bloq-btn-secondary">Cancel</button>
          <button onClick={submit} disabled={saving} className="bloq-btn-primary" data-testid="rb-submit">{saving ? "Saving…" : "Reschedule"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-[#82828c]">{label}</p>
      <p className="text-sm mt-0.5">{value}</p>
    </div>
  );
}

/* =====================================================================
 *  BOOKING LINKS
 * ===================================================================== */
function LinksPanel({ workspace }) {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [apts, setApts] = useState([]);
  const [forms, setForms] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [status, setStatus] = useState("active");
  const bulk = useBulkSelect();
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = async () => {
    if (!workspace) return;
    const [r, a, f, s] = await Promise.all([
      api.get(`/booking/links`, { params: { workspace_id: workspace.workspace_id, status } }),
      api.get(`/booking/appointment-types`, { params: { workspace_id: workspace.workspace_id } }),
      api.get(`/booking/forms`, { params: { workspace_id: workspace.workspace_id } }),
      api.get(`/booking/availability/schedules`, { params: { workspace_id: workspace.workspace_id } }),
    ]);
    setRows(r.data); setApts(a.data); setForms(f.data); setSchedules(s.data);
    bulk.clear();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace, status]);
  const copyLink = (slug) => {
    copyText(`${window.location.origin}/book/${slug}`)
      .then(() => toast.success("Public link copied"))
      .catch(() => toast.error("Couldn't copy — long-press the URL chip above to copy manually"));
  };
  const archive = async (l) => {
    if (!window.confirm(`Archive "${l.title}"?`)) return;
    try {
      await api.delete(`/booking/links/${l.link_id}`);
      toast.success("Archived — switch the filter to 'Archived' to find it."); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async (l) => {
    try {
      await api.post(`/booking/links/${l.link_id}/restore`);
      toast.success("Restored"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const hardDelete = async (l) => {
    if (!window.confirm(`Permanently delete "${l.title}"? This cannot be undone.`)) return;
    if (!window.confirm(`Are you absolutely sure? This deletes "${l.title}" forever.`)) return;
    try {
      await api.delete(`/booking/links/${l.link_id}`, { params: { hard: true } });
      toast.success("Deleted permanently"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#4a4a52]">Multiple booking links per workspace. Each supports its own appointment type, intake form, schedule, and policies.</p>
        <button onClick={() => { setEdit(null); setShow(true); }} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-link-btn">
          <Plus className="w-4 h-4" /> New link
        </button>
      </div>
      <StatusFilter value={status} onChange={setStatus} options={["active", "inactive", "archived", "all"]} count={rows.length} testid="link-status-filter" />
      <ProTip id="bookings.links.order">
        Set your <strong>appointment types</strong> BEFORE setting up your booking links.
      </ProTip>
      {rows.length === 0 ? (
        <EmptyCard icon={CalendarClock} title={`No ${status === "all" ? "" : status + " "}booking links`} desc={status === "archived" ? "Archive a link to see it here." : "Create your first link — a 15-min intro, a paid 60-min session, anything."} />
      ) : (
        <>
          <BulkSelectToolbar
            selectedCount={bulk.count}
            totalVisible={rows.length}
            onSelectAll={() => bulk.selectAll(rows.map((l) => l.link_id))}
            onClear={bulk.clear}
            onDelete={() => setBulkConfirm(true)}
            itemLabel="link"
            busy={bulkBusy}
            testidPrefix="links-bulk"
          />
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {rows.map((l) => {
              const checked = bulk.isSelected(l.link_id);
              return (
                <div key={l.link_id} className={`bloq-card p-5 flex flex-col gap-3 ${l.is_archived ? "opacity-60" : ""} ${checked ? "ring-2 ring-[#005e7a]" : ""}`} data-testid={`link-${l.link_id}`}>
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <button
                          type="button"
                          onClick={() => bulk.toggle(l.link_id)}
                          className="p-1 -m-1 mt-0.5 flex-shrink-0"
                          aria-label={checked ? "Unselect link" : "Select link"}
                          data-testid={`links-bulk-row-${l.link_id}`}
                        >
                          {checked
                            ? <CheckSquare className="w-4 h-4 text-[#005e7a]" />
                            : <Square className="w-4 h-4 text-[#82828c]" />}
                        </button>
                        <p className="bloq-headline text-lg min-w-0 break-words text-[#005e7a]">{l.title}</p>
                      </div>
                      <div className="flex flex-col gap-1 items-end shrink-0">
                        {l.is_archived && <span className="bloq-pill text-[10px] uppercase tracking-wider font-semibold bg-[#1a1a1f]/10 text-[#4a4a52]">Archived</span>}
                        {!l.is_archived && l.active === false && <span className="bloq-pill text-[10px] uppercase tracking-wider font-semibold bg-[#fef3c7] text-[#b45309]">Inactive</span>}
                      </div>
                    </div>
                    {/* Duration / location / classification line removed
                        per product — it was inherited from the link's
                        legacy single-AT fields and felt redundant on
                        multi-AT links (where the link itself spans
                        several services with different durations). The
                        approval / payment pills below still render so
                        guards stay visible. */}
                    {l.requires_approval && <span className="bloq-pill mt-2 text-[10px]" style={{ background: "rgba(180,83,9,0.15)", color: "#b45309" }}>Requires approval</span>}
                    {l.requires_payment && <span className="bloq-pill mt-2 text-[10px] ml-1" style={{ background: "rgba(180,83,9,0.15)", color: "#b45309" }}>Payment required</span>}
                  </div>
                  <a
                    href={`${window.location.origin}/book/${l.public_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs bg-[#f5efe6] p-2 rounded-md break-all block font-mono text-[#005e7a] hover:bg-[#005e7a]/10 hover:underline transition-colors"
                    data-testid={`open-link-${l.link_id}`}
                    title="Open booking link in a new tab"
                  >{`${window.location.origin}/book/${l.public_slug}`}</a>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => copyLink(l.public_slug)} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`copy-link-${l.link_id}`}><Copy className="w-3 h-3" /> Copy</button>
                    <button onClick={() => { setEdit(l); setShow(true); }} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`edit-link-${l.link_id}`}><Pencil className="w-3 h-3" /> Edit</button>
                    {l.is_archived ? (
                      <button onClick={() => restore(l)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#005e7a]" data-testid={`restore-link-${l.link_id}`}>Restore</button>
                    ) : (
                      <button onClick={() => archive(l)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#82828c]" data-testid={`archive-link-${l.link_id}`} title="Archive — hidden but recoverable">Archive</button>
                    )}
                    <button onClick={() => hardDelete(l)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#c43838] inline-flex items-center gap-1" data-testid={`delete-link-${l.link_id}`} title="Permanently delete — cannot be undone">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      <BulkDeleteConfirmDialog
        open={bulkConfirm}
        onClose={() => setBulkConfirm(false)}
        onConfirm={async () => {
          setBulkBusy(true);
          try {
            const r = await api.post(`/booking/links/bulk-delete`, {
              workspace_id: workspace.workspace_id,
              ids: bulk.ids,
            });
            toast.success(`Deleted ${r.data.deleted} link(s)`);
            setBulkConfirm(false);
            load();
          } catch (e) {
            toast.error(formatApiError(e) || "Bulk delete failed");
          } finally {
            setBulkBusy(false);
          }
        }}
        count={bulk.count}
        itemLabel="link"
        description="The selected booking links will be permanently deleted. Past bookings referencing them are unaffected."
        busy={bulkBusy}
        testidPrefix="links-bulk-delete"
      />
      <LinkDialog open={show} onOpenChange={setShow} link={edit} apts={apts} forms={forms} schedules={schedules} workspace={workspace} onSaved={() => { setShow(false); setEdit(null); load(); }} />
    </div>
  );
}

function LinkDialog({ open, onOpenChange, link, apts, forms, schedules, workspace, onSaved }) {
  const [form, setForm] = useState({
    title: "", description: "",
    public_slug: "", visibility: "public", active: true, timezone: "UTC",
    appointment_type_id: "", appointment_type_ids: [],
    daily_booking_limit: 0,
  });
  useEffect(() => {
    if (link) {
      // Migrate legacy singular appointment_type_id into the array if the
      // server hasn't sent appointment_type_ids yet.
      const ids = link.appointment_type_ids?.length
        ? link.appointment_type_ids
        : (link.appointment_type_id ? [link.appointment_type_id] : []);
      setForm({
        title: link.title || "",
        description: link.description || "",
        public_slug: link.public_slug || "",
        visibility: link.visibility || "public",
        active: link.active !== false,
        timezone: link.timezone || "UTC",
        appointment_type_id: ids[0] || "",
        appointment_type_ids: ids,
        daily_booking_limit: link.daily_booking_limit || 0,
      });
    } else {
      setForm({
        title: "", description: "",
        public_slug: "", visibility: "public", active: true, timezone: "UTC",
        appointment_type_id: "", appointment_type_ids: [], daily_booking_limit: 0,
      });
    }
    // eslint-disable-next-line
  }, [link, open]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const ids = form.appointment_type_ids || [];
    const payload = {
      ...form,
      workspace_id: workspace.workspace_id,
      appointment_type_ids: ids,
      appointment_type_id: ids[0] || null,
      public_slug: (form.public_slug || "").trim() || undefined,  // let backend auto-generate when blank
    };
    try {
      if (link) await api.patch(`/booking/links/${link.link_id}`, payload);
      else await api.post(`/booking/links`, payload);
      toast.success(link ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleApt = (id) => {
    setForm((f) => {
      const has = f.appointment_type_ids.includes(id);
      const next = has ? f.appointment_type_ids.filter((x) => x !== id) : [...f.appointment_type_ids, id];
      return { ...f, appointment_type_ids: next, appointment_type_id: next[0] || "" };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="link-dialog">
        <DialogHeader><DialogTitle>{link ? "Edit booking link" : "New booking link"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <Field label="Title" full>
            <input className="bloq-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="link-title" />
          </Field>
          <Field label="Custom URL" full>
            <div className="flex items-stretch">
              <span className="bloq-input rounded-r-none border-r-0 flex items-center text-[#82828c] text-xs px-2 bg-[#f5efe6]" data-testid="link-slug-prefix">/book/</span>
              <input
                className="bloq-input rounded-l-none"
                value={form.public_slug || ""}
                onChange={(e) => setForm({ ...form, public_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-") })}
                placeholder="auto-generated from title"
                data-testid="link-slug"
              />
            </div>
            <p className="text-[11px] text-[#82828c] mt-1.5">Lowercase letters, numbers, dashes (3-64 chars). Leave blank to auto-generate from the title.</p>
          </Field>
          <Field label="Description" full>
            <textarea rows={2} className="bloq-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Field label="Appointment types" full>
            {apts.length === 0 ? (
              <p className="text-xs text-[#82828c]">No appointment types yet — create one in the Appointment Types tab.</p>
            ) : (
              <div className="flex flex-col gap-1.5 max-h-44 overflow-y-auto bg-[#f5efe6] p-2 rounded-md" data-testid="link-apt-multi">
                {apts.map((a) => (
                  <label key={a.appointment_type_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.appointment_type_ids.includes(a.appointment_type_id)} onChange={() => toggleApt(a.appointment_type_id)} className="h-3.5 w-3.5" data-testid={`link-apt-${a.appointment_type_id}`} />
                    <span className="flex-1">{a.name}</span>
                    <span className="text-[10px] text-[#82828c]">{a.duration_minutes}m · {a.price_amount > 0 ? `${a.price_currency} ${a.price_amount}` : "Free"}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-[11px] text-[#82828c] mt-1.5">
              {form.appointment_type_ids.length === 0 && (
                <span className="text-[#b45309]">⚠ A link without appointment types can't be booked.</span>
              )}
              {form.appointment_type_ids.length === 1 && "Single-type link — visitors go straight to time selection."}
              {form.appointment_type_ids.length > 1 && `Visitors will pick one of ${form.appointment_type_ids.length} appointment types before choosing a time.`}
            </p>
          </Field>
          <Field label="Daily booking limit">
            <input type="number" min="0" className="bloq-input" value={form.daily_booking_limit} onChange={(e) => setForm({ ...form, daily_booking_limit: Number(e.target.value) })} data-testid="link-daily-limit" />
            <p className="text-[11px] text-[#82828c] mt-1">0 = unlimited. Caps total bookings made through this link per calendar day.</p>
          </Field>
          <Field label="Timezone">
            <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-60">
                {COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Visibility">
            <Select value={form.visibility || "public"} onValueChange={(v) => setForm({ ...form, visibility: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public (listed)</SelectItem>
                <SelectItem value="unlisted">Unlisted (direct link only)</SelectItem>
                <SelectItem value="private">Private (auth required)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <ToggleRow label="Active" v={form.active !== false} onChange={(v) => setForm({ ...form, active: v })} testid="link-active-toggle" />
          {/* Bundle B.1 — All scheduling fields (duration, buffers, min-notice,
              max-days, interval, intake form, availability schedule, location,
              approval, payment, allow_cancel/reschedule, prep/followup tasks,
              confirmation message) now live on the appointment type. The link
              is a thin "menu" that lists appointments. */}
          <Field label="" full>
            <div className="bg-[#f5efe6] p-3 rounded-md text-[11px] text-[#82828c] leading-relaxed" data-testid="link-inheritance-hint">
              <span className="font-medium text-[#1a1a1f]">Scheduling is set on each appointment type.</span> Duration, buffers, location, intake form, availability schedule, confirmation message, approval/payment requirements, and reschedule/cancel rules all live on the appointment. Edit them in the Appointment Types tab.
            </div>
          </Field>
        </div>
        <DialogFooter className="mt-4">
          <button onClick={submit} className="bloq-btn-primary" data-testid="link-save">{link ? "Save changes" : "Create link"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =====================================================================
 *  APPOINTMENT TYPES
 * ===================================================================== */
/* =====================================================================
 *  APPOINTMENT TYPES (now a sub-tabbed panel: Types / Categories / Locations)
 * ===================================================================== */
function AppointmentTypesPanel({ workspace }) {
  const [subtab, setSubtab] = useState("types");
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-1.5 bg-white border border-[#1a1a1f]/8 rounded-lg p-1 self-start" data-testid="apt-subtabs">
        {[
          { id: "types", label: "Appointment types" },
          { id: "categories", label: "Categories" },
          { id: "locations", label: "Locations" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubtab(t.id)}
            data-testid={`apt-subtab-${t.id}`}
            className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
              subtab === t.id ? "bg-[#1a1a1f] text-white" : "text-[#4a4a52] hover:bg-[#f5efe6]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {subtab === "types" && <ApptTypesTabContent workspace={workspace} />}
      {subtab === "categories" && <CategoriesPanel workspace={workspace} />}
      {subtab === "locations" && <LocationsPanel workspace={workspace} />}
    </div>
  );
}

function ApptTypesTabContent({ workspace }) {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [forms, setForms] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  // Status filter: active (default) | inactive | archived | all
  const [status, setStatus] = useState("active");
  const bulk = useBulkSelect();
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = async () => {
    if (!workspace) return;
    const [r, c, l, f, s] = await Promise.all([
      api.get(`/booking/appointment-types`, { params: { workspace_id: workspace.workspace_id, status } }),
      api.get(`/booking/categories`, { params: { workspace_id: workspace.workspace_id } }),
      api.get(`/booking/locations`, { params: { workspace_id: workspace.workspace_id } }),
      api.get(`/booking/forms`, { params: { workspace_id: workspace.workspace_id } }),
      api.get(`/booking/availability/schedules`, { params: { workspace_id: workspace.workspace_id } }),
    ]);
    setRows(r.data); setCategories(c.data); setLocations(l.data); setForms(f.data); setSchedules(s.data);
    bulk.clear();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace, status]);

  const archive = async (a) => {
    if (!window.confirm(`Archive "${a.name}"? You can still see it under archived items.`)) return;
    try {
      await api.delete(`/booking/appointment-types/${a.appointment_type_id}`);
      toast.success("Archived — switch the filter to 'Archived' to find it again."); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async (a) => {
    try {
      await api.post(`/booking/appointment-types/${a.appointment_type_id}/restore`);
      toast.success("Restored"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const hardDelete = async (a) => {
    if (!window.confirm(`Permanently delete "${a.name}"? This cannot be undone. Any booking links referencing it will be updated.`)) return;
    if (!window.confirm(`Are you absolutely sure? This deletes "${a.name}" forever.`)) return;
    try {
      await api.delete(`/booking/appointment-types/${a.appointment_type_id}`, { params: { hard: true } });
      toast.success("Deleted permanently");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const duplicate = async (a) => {
    await api.post(`/booking/appointment-types/${a.appointment_type_id}/duplicate`);
    toast.success("Duplicated"); load();
  };
  const copyDirectLink = (a) => {
    if (!a.public_slug) { toast.error("This appointment has no public slug yet — open Edit and save it."); return; }
    const url = `${window.location.origin}/a/${a.public_slug}`;
    copyText(url)
      .then(() => toast.success("Direct link copied"))
      .catch(() => toast.error("Couldn't copy — open the appointment to see the direct URL"));
  };

  const catName = (id) => categories.find((c) => c.category_id === id)?.name;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#4a4a52]">Each appointment type is independently bookable — has its own direct URL, scheduling rules, and price.</p>
        <button onClick={() => { setEdit(null); setShow(true); }} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-apt-btn">
          <Plus className="w-4 h-4" /> New appointment type
        </button>
      </div>
      <StatusFilter value={status} onChange={setStatus} options={["active", "inactive", "archived", "all"]} count={rows.length} testid="apt-status-filter" />
      <ProTip id="bookings.apt-types.order">
        Set up your <strong>availability</strong> and <strong>locations</strong> FIRST before creating your appointment types.
      </ProTip>
      {rows.length === 0 ? <EmptyCard icon={Layers} title={`No ${status === "all" ? "" : status + " "}appointment types`} desc={status === "archived" ? "Archive an appointment to see it here." : "e.g. '30-min consultation', 'Paid coaching session'."} /> : (
        <>
          <BulkSelectToolbar
            selectedCount={bulk.count}
            totalVisible={rows.length}
            onSelectAll={() => bulk.selectAll(rows.map((a) => a.appointment_type_id))}
            onClear={bulk.clear}
            onDelete={() => setBulkConfirm(true)}
            itemLabel="appointment type"
            itemLabelPlural="appointment types"
            busy={bulkBusy}
            testidPrefix="apt-bulk"
          />
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {rows.map((a) => {
              const checked = bulk.isSelected(a.appointment_type_id);
              return (
                <div key={a.appointment_type_id} className={`bloq-card p-5 flex flex-col gap-3 border-l-4 ${a.archived_at ? "opacity-60" : ""} ${checked ? "ring-2 ring-[#005e7a]" : ""}`} style={{ borderLeftColor: a.color || "#005e7a" }} data-testid={`apt-${a.appointment_type_id}`}>
                  {/* Host-uploaded thumbnail. Same 4:3 aspect ratio as
                      the public booking page so what the host sees here
                      matches what their invitees see. */}
                  {a.image_url && (
                    <div className="aspect-[4/3] -mx-5 -mt-5 mb-1 overflow-hidden rounded-t-md bg-[#f5efe6] border-b border-[#1a1a1f]/8">
                      <img
                        src={a.image_url}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                        data-testid={`apt-thumb-${a.appointment_type_id}`}
                      />
                    </div>
                  )}
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <button
                          type="button"
                          onClick={() => bulk.toggle(a.appointment_type_id)}
                          className="p-1 -m-1 mt-0.5 flex-shrink-0"
                          aria-label={checked ? "Unselect appointment" : "Select appointment"}
                          data-testid={`apt-bulk-row-${a.appointment_type_id}`}
                        >
                          {checked
                            ? <CheckSquare className="w-4 h-4 text-[#005e7a]" />
                            : <Square className="w-4 h-4 text-[#82828c]" />}
                        </button>
                        <p className="bloq-headline text-lg min-w-0 break-words text-[#005e7a]">{a.name}</p>
                      </div>
                      <div className="flex flex-col gap-1 items-end shrink-0">
                        {a.archived_at && <span className="bloq-pill text-[10px] uppercase tracking-wider font-semibold bg-[#1a1a1f]/10 text-[#4a4a52]">Archived</span>}
                        {!a.archived_at && a.active === false && <span className="bloq-pill text-[10px] uppercase tracking-wider font-semibold bg-[#fef3c7] text-[#b45309]">Inactive</span>}
                      </div>
                    </div>
                    {a.public_slug && (
                      <a href={`/a/${a.public_slug}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#82828c] mt-1 font-mono truncate block hover:text-[#005e7a] hover:underline" data-testid={`apt-slug-${a.appointment_type_id}`} title="Open public booking page in new tab">/a/{a.public_slug}</a>
                    )}
                    <p className="text-xs text-[#82828c] mt-1">
                      {a.duration_minutes}m · {a.price_amount > 0 ? `${a.price_currency} ${a.price_amount}` : "Free"}
                      {a.sale_price_amount != null && a.sale_price_amount !== "" && a.sale_price_amount !== a.price_amount && (
                        <> · <span className="line-through opacity-60">{a.price_currency} {a.price_amount}</span> {a.price_currency} {a.sale_price_amount}</>
                      )}
                    </p>
                    <p className="text-xs text-[#82828c] mt-1">
                      {a.public_or_private === "private" ? (
                        <span className="inline-flex items-center gap-1 bloq-pill text-[10px] uppercase tracking-wider font-semibold bg-[#fef3c7] text-[#b45309]" title="Hidden from public booking link menus. Reachable only via /a/<slug> direct URL.">Private · Direct link only</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bloq-pill text-[10px] uppercase tracking-wider font-semibold bg-[#dcfce7] text-[#15803d]" title="Visible in any booking link menus this appointment is attached to.">Public · Listed</span>
                      )}
                      {a.classification && a.classification !== "individual" && <> · {a.classification}</>}
                      {catName(a.category_id) && <> · <span className="text-[#005e7a]">{catName(a.category_id)}</span></>}
                    </p>
                  </div>
                  {a.description && <p className="text-sm text-[#4a4a52] line-clamp-2">{a.description}</p>}
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => { setEdit(a); setShow(true); }} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid={`edit-apt-${a.appointment_type_id}`}>Edit</button>
                    <button onClick={() => copyDirectLink(a)} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`copy-apt-${a.appointment_type_id}`} title={`/a/${a.public_slug}`}>
                      <Copy className="w-3 h-3" /> Direct link
                    </button>
                    <button onClick={() => duplicate(a)} className="bloq-btn-secondary text-xs px-3 py-1.5">Duplicate</button>
                    {a.archived_at ? (
                      <button onClick={() => restore(a)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#005e7a]" data-testid={`restore-apt-${a.appointment_type_id}`} title="Restore from archive">Restore</button>
                    ) : (
                      <button onClick={() => archive(a)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#82828c]" data-testid={`archive-apt-${a.appointment_type_id}`} title="Archive — hidden but recoverable">Archive</button>
                    )}
                    <button onClick={() => hardDelete(a)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#c43838] inline-flex items-center gap-1" data-testid={`delete-apt-${a.appointment_type_id}`} title="Permanently delete — cannot be undone">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      <BulkDeleteConfirmDialog
        open={bulkConfirm}
        onClose={() => setBulkConfirm(false)}
        onConfirm={async () => {
          setBulkBusy(true);
          try {
            const r = await api.post(`/booking/appointment-types/bulk-delete`, {
              workspace_id: workspace.workspace_id,
              ids: bulk.ids,
            });
            toast.success(`Deleted ${r.data.deleted} appointment type(s)`);
            setBulkConfirm(false);
            load();
          } catch (e) {
            toast.error(formatApiError(e) || "Bulk delete failed");
          } finally {
            setBulkBusy(false);
          }
        }}
        count={bulk.count}
        itemLabel="appointment type"
        itemLabelPlural="appointment types"
        description="Permanently delete the selected appointment types. Any booking links referencing them will have them unlinked. Past bookings are unaffected."
        busy={bulkBusy}
        testidPrefix="apt-bulk-delete"
      />
      <AppointmentTypeDialog
        open={show} onOpenChange={setShow} apt={edit}
        workspace={workspace} categories={categories} locations={locations}
        forms={forms} schedules={schedules}
        onSaved={() => { setShow(false); setEdit(null); load(); }}
      />
    </div>
  );
}

/* ----- Categories sub-panel ------------------------------------------- */
function CategoriesPanel({ workspace }) {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = async () => {
    if (!workspace) return;
    const r = await api.get(`/booking/categories`, { params: { workspace_id: workspace.workspace_id } });
    setRows(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);
  const archive = async (c) => {
    if (!window.confirm(`Archive category "${c.name}"?`)) return;
    await api.delete(`/booking/categories/${c.category_id}`);
    toast.success("Archived"); load();
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#4a4a52]">Group appointment types into categories so the public booking page can organize them.</p>
        <button onClick={() => { setEdit(null); setShow(true); }} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-category-btn">
          <Plus className="w-4 h-4" /> New category
        </button>
      </div>
      {rows.length === 0 ? <EmptyCard icon={Layers} title="No categories yet" desc="e.g. 'Consulting', 'Coaching', 'Classes'." /> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map((c) => (
            <div key={c.category_id} className="bloq-card p-4 flex items-center justify-between gap-3 border-l-4" style={{ borderLeftColor: c.color || "#005e7a" }} data-testid={`cat-${c.category_id}`}>
              <div className="min-w-0">
                <p className="bloq-headline text-base truncate">{c.name}</p>
                {c.description && <p className="text-xs text-[#82828c] truncate">{c.description}</p>}
                <p className="text-[10px] text-[#82828c] mt-1">{c.active ? "Active" : "Inactive"} · sort {c.sort_order}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEdit(c); setShow(true); }} className="bloq-btn-secondary text-xs px-2 py-1" data-testid={`edit-cat-${c.category_id}`}>Edit</button>
                <button onClick={() => archive(c)} className="bloq-btn-secondary text-xs px-2 py-1 text-[#c43838]"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <CategoryDialog open={show} onOpenChange={setShow} cat={edit} workspace={workspace} onSaved={() => { setShow(false); setEdit(null); load(); }} />
    </div>
  );
}

function CategoryDialog({ open, onOpenChange, cat, workspace, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", color: "#005e7a", sort_order: 0, active: true });
  useEffect(() => {
    if (cat) setForm({ name: cat.name || "", description: cat.description || "", color: cat.color || "#005e7a", sort_order: cat.sort_order || 0, active: cat.active !== false });
    else setForm({ name: "", description: "", color: "#005e7a", sort_order: 0, active: true });
  }, [cat, open]);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (cat) await api.patch(`/booking/categories/${cat.category_id}`, form);
      else await api.post(`/booking/categories`, { ...form, workspace_id: workspace.workspace_id });
      toast.success(cat ? "Updated" : "Created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cat-dialog">
        <DialogHeader><DialogTitle>{cat ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Name" full><input className="bloq-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" /></Field>
          <Field label="Description" full><input className="bloq-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Color"><input type="color" className="bloq-input h-10" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></Field>
          <Field label="Sort order"><input type="number" className="bloq-input" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></Field>
          <ToggleRow label="Active" v={form.active} onChange={(v) => setForm({ ...form, active: v })} />
        </div>
        <DialogFooter className="mt-4"><button onClick={submit} className="bloq-btn-primary" data-testid="cat-save">{cat ? "Save" : "Create"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----- Locations sub-panel -------------------------------------------- */
function LocationsPanel({ workspace }) {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = async () => {
    if (!workspace) return;
    const r = await api.get(`/booking/locations`, { params: { workspace_id: workspace.workspace_id } });
    setRows(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);
  const archive = async (l) => {
    if (!window.confirm(`Archive location "${l.name}"?`)) return;
    await api.delete(`/booking/locations/${l.location_id}`);
    toast.success("Archived"); load();
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#4a4a52]">Reusable locations you can attach to appointment types.</p>
        <button onClick={() => { setEdit(null); setShow(true); }} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-location-btn">
          <Plus className="w-4 h-4" /> New location
        </button>
      </div>
      {rows.length === 0 ? <EmptyCard icon={MapPin} title="No locations yet" desc="e.g. 'Main office', 'Studio B', 'Online'." /> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map((l) => (
            <div key={l.location_id} className="bloq-card p-4 flex items-start justify-between gap-3" data-testid={`loc-${l.location_id}`}>
              <div className="min-w-0">
                <p className="bloq-headline text-base truncate text-[#005e7a]">{l.name}</p>
                <p className="text-xs text-[#82828c] mt-1">{LOCATION_LABEL[l.type] || l.type}</p>
                {l.address && <p className="text-xs text-[#82828c] truncate">{l.address_formatted || l.address}</p>}
                {l.external_link && <p className="text-xs text-[#005e7a] truncate">{l.external_link}</p>}
                <p className="text-[10px] text-[#82828c] mt-1">{l.active ? "Active" : "Inactive"}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEdit(l); setShow(true); }} className="bloq-btn-secondary text-xs px-2 py-1" data-testid={`edit-loc-${l.location_id}`}>Edit</button>
                <button onClick={() => archive(l)} className="bloq-btn-secondary text-xs px-2 py-1 text-[#c43838]"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <LocationDialog open={show} onOpenChange={setShow} loc={edit} workspace={workspace} onSaved={() => { setShow(false); setEdit(null); load(); }} />
    </div>
  );
}

function LocationDialog({ open, onOpenChange, loc, workspace, onSaved }) {
  const [form, setForm] = useState({
    name: "", type: "online",
    address_struct: { ...EMPTY_ADDRESS },
    phone_instructions: "",
    online_behavior: "auto_bloq_video", external_link: "",
    custom_instructions: "", active: true,
  });
  useEffect(() => {
    if (loc) setForm({
      name: loc.name || "", type: loc.type || "online",
      // Prefer the structured value; fall back to a minimal struct holding
      // the legacy flat `address` string in line1 so old data is editable.
      address_struct: loc.address_struct || { ...EMPTY_ADDRESS, line1: loc.address || "" },
      phone_instructions: loc.phone_instructions || "",
      online_behavior: loc.online_behavior || "auto_bloq_video",
      external_link: loc.external_link || "", custom_instructions: loc.custom_instructions || "",
      active: loc.active !== false,
    });
    else setForm({
      name: "", type: "online",
      address_struct: { ...EMPTY_ADDRESS },
      phone_instructions: "",
      online_behavior: "auto_bloq_video", external_link: "",
      custom_instructions: "", active: true,
    });
  }, [loc, open]);
  const showAddress = ["in_person", "hybrid", "custom"].includes(form.type);
  const showPhone = form.type === "phone";
  const showOnlineBehavior = ["online", "hybrid"].includes(form.type);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    // Build the payload — backend composes `address` from `address_struct`
    // for the geocoder + legacy callers. Send both shapes so older
    // consumers still see a flat string until they migrate.
    const payload = {
      ...form,
      address: showAddress ? composeAddress(form.address_struct) : "",
    };
    try {
      if (loc) await api.patch(`/booking/locations/${loc.location_id}`, payload);
      else await api.post(`/booking/locations`, { ...payload, workspace_id: workspace.workspace_id });
      toast.success(loc ? "Updated" : "Created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="loc-dialog">
        <DialogHeader><DialogTitle>{loc ? "Edit location" : "New location"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Name" full><input className="bloq-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="loc-name" /></Field>
          <Field label="Type" full>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="loc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in_person">In person</SelectItem>
                <SelectItem value="online">Online (video)</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {showAddress && (
            <div className="col-span-2">
              <AddressFields
                value={form.address_struct}
                onChange={(s) => setForm({ ...form, address_struct: s })}
                testid="loc-addr"
              />
            </div>
          )}
          {showPhone && (
            <Field label="Phone instructions" full><textarea rows={2} className="bloq-input" placeholder="e.g. host will call you on the number you provide" value={form.phone_instructions} onChange={(e) => setForm({ ...form, phone_instructions: e.target.value })} /></Field>
          )}
          {showOnlineBehavior && (
            <Field label="Online behavior" full>
              <Select value={form.online_behavior} onValueChange={(v) => setForm({ ...form, online_behavior: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto_bloq_video">Auto-create Bloq video link</SelectItem>
                  <SelectItem value="external_link">Use the external link below</SelectItem>
                  <SelectItem value="none">None — host shares manually</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
          {showOnlineBehavior && form.online_behavior === "external_link" && (
            <Field label="External link" full><input className="bloq-input" placeholder="https://zoom.us/…" value={form.external_link} onChange={(e) => setForm({ ...form, external_link: e.target.value })} /></Field>
          )}
          <Field label="Custom instructions" full><textarea rows={2} className="bloq-input" value={form.custom_instructions} onChange={(e) => setForm({ ...form, custom_instructions: e.target.value })} /></Field>
          <ToggleRow label="Active" v={form.active} onChange={(v) => setForm({ ...form, active: v })} />
        </div>
        <DialogFooter className="mt-4"><button onClick={submit} className="bloq-btn-primary" data-testid="loc-save">{loc ? "Save" : "Create"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApptImagePicker({ apt, form, setForm, workspace }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  // The endpoint stores the image against the appointment row, so the
  // host has to save the appointment at least once before uploading.
  // First-time creators see a helper note pointing them to "Save then
  // re-open" — keeps the flow understandable without us having to do a
  // pre-create.
  if (!apt?.appointment_type_id) {
    return (
      <p className="text-xs text-[#82828c] mt-2" data-testid="apt-image-needs-save">
        Save the appointment type first — then re-open it to attach a thumbnail image.
      </p>
    );
  }
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow picking the same file again
    if (!file) return;
    if (!/^image\//i.test(file.type)) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5 MB or smaller");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("workspace_id", workspace.workspace_id);
      const r = await api.post(
        `/booking/appointment-types/${apt.appointment_type_id}/image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setForm({ ...form, image_url: r.data.image_url });
      toast.success("Image updated");
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  const onRemove = async () => {
    setBusy(true);
    try {
      await api.delete(`/booking/appointment-types/${apt.appointment_type_id}/image`, {
        params: { workspace_id: workspace.workspace_id },
      });
      setForm({ ...form, image_url: "" });
      toast.success("Image removed");
    } catch (err) {
      toast.error(formatApiError(err) || "Remove failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-4 mt-1">
      <div
        className="w-32 aspect-[4/3] rounded-md overflow-hidden bg-[#f5efe6] border border-[#1a1a1f]/10 flex items-center justify-center text-xs text-[#82828c] flex-shrink-0"
        data-testid="apt-image-preview"
      >
        {form.image_url ? (
          <img src={form.image_url} alt="" className="w-full h-full object-cover" />
        ) : (
          "No image"
        )}
      </div>
      <div className="flex flex-col gap-2 min-w-0">
        <p className="text-xs text-[#82828c]">
          Images with a 4:3 ratio look best. Max 5 MB. Renders identically here, on
          booking links, and on the public <code>/a/&lt;slug&gt;</code> page.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="bloq-btn-secondary text-xs px-3 py-1.5"
            data-testid="apt-image-upload"
          >
            {busy ? "Working…" : form.image_url ? "Replace image" : "Upload image"}
          </button>
          {form.image_url && (
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#c43838]"
              data-testid="apt-image-remove"
            >
              Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
          data-testid="apt-image-input"
        />
      </div>
    </div>
  );
}

function AppointmentTypeDialog({ open, onOpenChange, apt, workspace, categories = [], locations = [], forms = [], schedules = [], onSaved }) {
  const [form, setForm] = useState({
    name: "", description: "", category: "", duration_minutes: 30,
    price_amount: 0, sale_price_amount: "", price_currency: "USD", color: "#005e7a",
    public_or_private: "public",
    public_slug: "", active: true,
    default_buffer_before_minutes: 0, default_buffer_after_minutes: 0,
    default_minimum_notice_minutes: 0, default_maximum_days_in_future: 60,
    default_start_time_interval_minutes: 15, default_confirmation_message: "",
    auto_create_prep_task: false, auto_create_followup_task: false,
    // Bundle B.1 — moved off booking links:
    booking_mode: "individual",
    requires_approval: false, requires_payment: false,
    allow_reschedule: true, allow_cancel: true,
    minimum_cancel_notice_minutes: 0, minimum_reschedule_notice_minutes: 0,
    payment_rule_id: "",
    daily_booking_limit: 0,
    phone_required: false,
    image_url: "",
    default_booking_form_id: "",
    default_availability_schedule_id: "",
    // Phase B — per-apt payment configuration:
    payment_mode: "none",       // none | deposit | full
    deposit_amount: 0,
    payment_capture: "immediate", // immediate | manual
    payment_method: "stripe",     // stripe | offline | either
    offline_payment_instructions: "",
    // Bundle A:
    category_id: "", classification: "individual",
    location_ids: [], default_location_id: "",
    max_participants: 1,
  });
  useEffect(() => {
    if (apt) {
      setForm((f) => ({
        ...f, ...apt,
        public_slug: apt.public_slug || "",
        active: apt.active !== false,
        sale_price_amount: apt.sale_price_amount ?? "",
        category_id: apt.category_id || "",
        classification: apt.classification || (apt.is_group_class ? "group" : "individual"),
        location_ids: apt.location_ids || [],
        default_location_id: apt.default_location_id || "",
        default_booking_form_id: apt.default_booking_form_id || "",
        default_availability_schedule_id: apt.default_availability_schedule_id || "",
        payment_rule_id: apt.payment_rule_id || "",
        // Phase B — payment fields (with safe fallbacks for legacy rows):
        payment_mode: apt.payment_mode || (apt.requires_payment ? "full" : "none"),
        deposit_amount: apt.deposit_amount ?? 0,
        payment_capture: apt.payment_capture || "immediate",
        payment_method: apt.payment_method || "stripe",
        offline_payment_instructions: apt.offline_payment_instructions || "",
      }));
    } else {
      setForm((f) => ({
        ...f, name: "", description: "", duration_minutes: 30, price_amount: 0, sale_price_amount: "",
        public_slug: "", active: true,
        category_id: "", classification: "individual", location_ids: [], default_location_id: "",
        default_booking_form_id: "", default_availability_schedule_id: "",
        payment_rule_id: "", daily_booking_limit: 0,
        requires_approval: false, requires_payment: false,
        allow_reschedule: true, allow_cancel: true,
        default_minimum_notice_minutes: 0, default_maximum_days_in_future: 60,
        default_buffer_before_minutes: 0, default_buffer_after_minutes: 0,
        default_start_time_interval_minutes: 15,
        default_confirmation_message: "",
        auto_create_prep_task: false, auto_create_followup_task: false,
        // Reset visibility, image, phone-required, and booking-mode too —
        // otherwise editing one apt then opening "New" silently inherits
        // the previous apt's image_url / phone_required / mode. Each
        // appointment is supposed to be fully independent.
        public_or_private: "public",
        image_url: "",
        phone_required: false,
        booking_mode: "individual",
        max_participants: 1,
        color: "#005e7a",
        minimum_cancel_notice_minutes: 0,
        minimum_reschedule_notice_minutes: 0,
        payment_mode: "none",
        deposit_amount: 0,
        payment_capture: "immediate",
        payment_method: "stripe",
        offline_payment_instructions: "",
      }));
    }
    // eslint-disable-next-line
  }, [apt, open]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    // Phase B — keep requires_payment + payment_mode coherent regardless of which control the user touched.
    const requires_payment = form.payment_mode && form.payment_mode !== "none";
    if (form.payment_mode === "deposit") {
      const dep = Number(form.deposit_amount || 0);
      if (dep <= 0) { toast.error("Deposit amount must be greater than 0"); return; }
      if (form.price_amount && dep > Number(form.price_amount)) {
        toast.error("Deposit can't exceed the price"); return;
      }
    }
    const payload = {
      ...form,
      requires_payment,
      sale_price_amount: form.sale_price_amount === "" ? null : Number(form.sale_price_amount),
      category_id: form.category_id || null,
      default_location_id: form.default_location_id || null,
      default_booking_form_id: form.default_booking_form_id || null,
      default_availability_schedule_id: form.default_availability_schedule_id || null,
      payment_rule_id: form.payment_rule_id || null,
      public_slug: (form.public_slug || "").trim() || undefined,
      is_group_class: ["group", "class", "workshop", "webinar"].includes(form.classification),
    };
    try {
      if (apt) await api.patch(`/booking/appointment-types/${apt.appointment_type_id}`, payload);
      else await api.post(`/booking/appointment-types`, { ...payload, workspace_id: workspace.workspace_id });
      toast.success(apt ? "Updated" : "Created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleLocation = (id) => {
    setForm((f) => {
      const has = f.location_ids.includes(id);
      const next = has ? f.location_ids.filter((x) => x !== id) : [...f.location_ids, id];
      const dflt = next.includes(f.default_location_id) ? f.default_location_id : (next[0] || "");
      return { ...f, location_ids: next, default_location_id: dflt };
    });
  };
  const isGroup = ["group", "class", "workshop", "webinar"].includes(form.classification);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="apt-dialog">
        <DialogHeader><DialogTitle>{apt ? "Edit appointment type" : "New appointment type"}</DialogTitle></DialogHeader>

        <p className="text-[10px] uppercase tracking-widest text-[#82828c] mt-3">Basics</p>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Field label="Name" full><input className="bloq-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apt-name" /></Field>
          <Field label="Custom URL" full>
            <div className="flex items-stretch">
              <span className="bloq-input rounded-r-none border-r-0 flex items-center text-[#82828c] text-xs px-2 bg-[#f5efe6]" data-testid="apt-slug-prefix">/a/</span>
              <input
                className="bloq-input rounded-l-none"
                value={form.public_slug || ""}
                onChange={(e) => setForm({ ...form, public_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-") })}
                placeholder="auto-generated from name"
                data-testid="apt-slug"
              />
            </div>
            <p className="text-[11px] text-[#82828c] mt-1.5">Lowercase letters, numbers, dashes (3-64 chars). This is the direct booking URL.</p>
          </Field>
          <Field label="Description" full><textarea rows={2} className="bloq-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Category">
            <Select value={form.category_id || "__none__"} onValueChange={(v) => setForm({ ...form, category_id: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="apt-category"><SelectValue placeholder="No category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No category</SelectItem>
                {categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Classification">
            <Select value={form.classification} onValueChange={(v) => setForm({ ...form, classification: v })}>
              <SelectTrigger data-testid="apt-classification"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="group">Group</SelectItem>
                <SelectItem value="class">Class</SelectItem>
                <SelectItem value="workshop">Workshop</SelectItem>
                <SelectItem value="webinar">Webinar</SelectItem>
                <SelectItem value="consultation">Consultation</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Color"><input type="color" className="bloq-input h-10" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></Field>
          <Field label="Visibility" help="Public — appears in any booking link menu. Private — bookable only via the direct /a/<slug> URL.">
            <Select value={form.public_or_private} onValueChange={(v) => setForm({ ...form, public_or_private: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public · Listed in link menus</SelectItem>
                <SelectItem value="private">Private · Direct link only</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        {/* Thumbnail image. Square is ideal, but the image is rendered
            at 4:3 in both the host's admin view and the public booking
            page (object-cover) so any sane aspect works. Apparent right
            after creation — apt must already exist (i.e. editing) so
            we have an appointment_type_id to attach the file to. */}
        <p className="text-[10px] uppercase tracking-widest text-[#82828c] mt-5">Thumbnail image</p>
        <ApptImagePicker
          apt={apt}
          form={form}
          setForm={setForm}
          workspace={workspace}
        />

        <p className="text-[10px] uppercase tracking-widest text-[#82828c] mt-5">Pricing & duration</p>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Field label="Duration" help={form.duration_minutes >= 60 ? `${Math.floor(form.duration_minutes / 60)}h ${form.duration_minutes % 60}m total` : null}>
            <div className="flex gap-2 items-center">
              <input
                type="number" min="0" className="bloq-input flex-1"
                aria-label="Hours"
                value={Math.floor((form.duration_minutes || 0) / 60)}
                onChange={(e) => {
                  const h = Math.max(0, Number(e.target.value) || 0);
                  const m = (form.duration_minutes || 0) % 60;
                  setForm({ ...form, duration_minutes: h * 60 + m });
                }}
                data-testid="apt-duration-hours"
              />
              <span className="text-xs text-[#82828c]" aria-hidden="true">h</span>
              <input
                type="number" min="0" max="59" className="bloq-input flex-1"
                aria-label="Minutes"
                value={(form.duration_minutes || 0) % 60}
                onChange={(e) => {
                  const m = Math.max(0, Math.min(59, Number(e.target.value) || 0));
                  const h = Math.floor((form.duration_minutes || 0) / 60);
                  setForm({ ...form, duration_minutes: h * 60 + m });
                }}
                data-testid="apt-duration-minutes"
              />
              <span className="text-xs text-[#82828c]" aria-hidden="true">m</span>
            </div>
          </Field>
          <Field label="Start interval (min)"><input type="number" className="bloq-input" value={form.default_start_time_interval_minutes} onChange={(e) => setForm({ ...form, default_start_time_interval_minutes: Number(e.target.value) })} /></Field>
          <Field label="Price">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <input type="number" step="0.01" className="bloq-input w-full" value={form.price_amount} onChange={(e) => setForm({ ...form, price_amount: Number(e.target.value) })} />
              </div>
              <div className="w-24">
                <label className="text-[10px] uppercase tracking-widest text-[#82828c] block mb-1">Currency</label>
                <input
                  className="bloq-input w-full"
                  value={form.price_currency}
                  onChange={(e) => setForm({ ...form, price_currency: e.target.value.toUpperCase() })}
                  maxLength={3}
                  placeholder="USD"
                  data-testid="apt-price-currency"
                />
              </div>
            </div>
          </Field>
          <Field label="Sale price (optional)">
            <input type="number" step="0.01" className="bloq-input" placeholder="blank = no sale" value={form.sale_price_amount} onChange={(e) => setForm({ ...form, sale_price_amount: e.target.value })} data-testid="apt-sale-price" />
          </Field>
          {isGroup && (
            <Field label="Max participants" full><input type="number" min="2" className="bloq-input" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: Number(e.target.value) })} data-testid="apt-max-participants" /></Field>
          )}
        </div>

        <p className="text-[10px] uppercase tracking-widest text-[#82828c] mt-5">Locations</p>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Field label="Allowed locations" full>
            {locations.length === 0 ? (
              <p className="text-xs text-[#82828c]">No locations defined yet. Add them in the Locations sub-tab.</p>
            ) : (
              <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto bg-[#f5efe6] p-2 rounded-md">
                {locations.map((l) => (
                  <label key={l.location_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.location_ids.includes(l.location_id)} onChange={() => toggleLocation(l.location_id)} className="h-3.5 w-3.5" data-testid={`apt-loc-${l.location_id}`} />
                    <span className="flex-1">{l.name}</span>
                    <span className="text-[10px] text-[#82828c]">{LOCATION_LABEL[l.type] || l.type}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-[11px] text-[#82828c] mt-1.5">When multiple locations are selected, visitors pick one during booking.</p>
          </Field>
          {form.location_ids.length > 1 && (
            <Field label="Default location" full>
              <Select value={form.default_location_id || form.location_ids[0]} onValueChange={(v) => setForm({ ...form, default_location_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {form.location_ids.map((id) => {
                    const l = locations.find((x) => x.location_id === id);
                    return l ? <SelectItem key={id} value={id}>{l.name}</SelectItem> : null;
                  })}
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>

        <p className="text-[10px] uppercase tracking-widest text-[#82828c] mt-5">Scheduling rules</p>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Field label="Booking mode">
            <Select value={form.booking_mode} onValueChange={(v) => setForm({ ...form, booking_mode: v })}>
              <SelectTrigger data-testid="apt-booking-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="round_robin">Round-robin (team)</SelectItem>
                <SelectItem value="collective">Collective (team)</SelectItem>
                <SelectItem value="group">Group</SelectItem>
                <SelectItem value="class">Class</SelectItem>
                <SelectItem value="recurring">Recurring</SelectItem>
                <SelectItem value="approval_required">Approval required</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Availability schedule">
            <Select value={form.default_availability_schedule_id || "none"} onValueChange={(v) => setForm({ ...form, default_availability_schedule_id: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Default" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Default schedule</SelectItem>
                {schedules.map((s) => <SelectItem key={s.schedule_id} value={s.schedule_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Intake form (optional)">
            <Select value={form.default_booking_form_id || "none"} onValueChange={(v) => setForm({ ...form, default_booking_form_id: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {forms.map((f) => <SelectItem key={f.form_id} value={f.form_id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Min notice (min)"><input type="number" className="bloq-input" value={form.default_minimum_notice_minutes} onChange={(e) => setForm({ ...form, default_minimum_notice_minutes: Number(e.target.value) })} data-testid="apt-min-notice" /></Field>
          <Field label="Buffer before (min)"><input type="number" className="bloq-input" value={form.default_buffer_before_minutes} onChange={(e) => setForm({ ...form, default_buffer_before_minutes: Number(e.target.value) })} /></Field>
          <Field label="Buffer after (min)"><input type="number" className="bloq-input" value={form.default_buffer_after_minutes} onChange={(e) => setForm({ ...form, default_buffer_after_minutes: Number(e.target.value) })} /></Field>
          <Field label="Max days ahead"><input type="number" className="bloq-input" value={form.default_maximum_days_in_future} onChange={(e) => setForm({ ...form, default_maximum_days_in_future: Number(e.target.value) })} /></Field>
          <Field label="Daily booking limit"><input type="number" min="0" className="bloq-input" value={form.daily_booking_limit} onChange={(e) => setForm({ ...form, daily_booking_limit: Number(e.target.value) })} data-testid="apt-daily-limit" /></Field>
          <Field label="Confirmation message" full><textarea rows={2} className="bloq-input" value={form.default_confirmation_message} onChange={(e) => setForm({ ...form, default_confirmation_message: e.target.value })} /></Field>
        </div>

        <p className="text-[10px] uppercase tracking-widest text-[#82828c] mt-5">Payments</p>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Field label="Payment mode">
            <Select value={form.payment_mode || "none"} onValueChange={(v) => setForm({ ...form, payment_mode: v, requires_payment: v !== "none" })}>
              <SelectTrigger data-testid="apt-payment-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No payment required</SelectItem>
                <SelectItem value="deposit">Take a deposit at booking</SelectItem>
                <SelectItem value="full">Charge full price at booking</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#82828c] mt-1.5">
              {form.payment_mode === "deposit"
                ? "Deposit collected at booking. Remaining balance auto-invoiced when you mark the appointment complete."
                : form.payment_mode === "full"
                  ? `Full price (${form.price_currency} ${form.price_amount || 0}) charged at booking.`
                  : "No card collected. Bookings confirm immediately."}
            </p>
          </Field>
          {form.payment_mode === "deposit" && (
            <Field label={`Deposit amount (${form.price_currency || "USD"})`}>
              <input
                type="number"
                step="0.01"
                min="0"
                max={form.price_amount || undefined}
                className="bloq-input"
                value={form.deposit_amount}
                onChange={(e) => setForm({ ...form, deposit_amount: Number(e.target.value) })}
                data-testid="apt-deposit-amount"
              />
              <p className="text-[11px] text-[#82828c] mt-1.5">
                Must be less than or equal to the price ({form.price_currency} {form.price_amount || 0}).
              </p>
            </Field>
          )}
          {form.payment_mode !== "none" && (
            <Field label="When to capture">
              <Select value={form.payment_capture || "immediate"} onValueChange={(v) => setForm({ ...form, payment_capture: v })}>
                <SelectTrigger data-testid="apt-payment-capture"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Charge immediately at booking</SelectItem>
                  <SelectItem value="manual">Card on file — charge when I mark complete</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#82828c] mt-1.5">
                {form.payment_capture === "manual"
                  ? "Card is authorized but not charged until you confirm the appointment happened."
                  : "Card is charged when the booking is created."}
              </p>
            </Field>
          )}
          {form.payment_mode !== "none" && (
            <Field label="Payment method">
              <Select value={form.payment_method || "stripe"} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="apt-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="stripe">Online — Stripe</SelectItem>
                  <SelectItem value="offline">Offline — cash, check, Venmo, etc.</SelectItem>
                  <SelectItem value="either">Either — invitee picks</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#82828c] mt-1.5">
                {form.payment_method === "offline"
                  ? "Booking confirms immediately. You record the actual payment from the Payments tab once it lands."
                  : form.payment_method === "either"
                    ? "Visitor sees both options at checkout — Stripe button or your offline instructions."
                    : "Visitor pays online via Stripe Checkout before the booking confirms."}
              </p>
            </Field>
          )}
          {form.payment_mode !== "none" && form.payment_method !== "stripe" && (
            <Field label="Offline payment instructions" full>
              <textarea
                rows={2}
                className="bloq-input"
                placeholder="e.g., Venmo @bloq-host · Pay at appointment · Mail check to PO Box 123"
                value={form.offline_payment_instructions}
                onChange={(e) => setForm({ ...form, offline_payment_instructions: e.target.value })}
                data-testid="apt-offline-instructions"
              />
              <p className="text-[11px] text-[#82828c] mt-1.5">
                Shown to the invitee on the confirmation page + included in their booking-confirmation email.
              </p>
            </Field>
          )}
          {form.payment_mode !== "none" && form.payment_method !== "offline" && (
            <div className="col-span-2 bg-[#f5efe6] p-3 rounded-md text-xs text-[#4a4a52]" data-testid="apt-payment-stripe-hint">
              <span className="font-medium">Stripe coming soon.</span> Online card payments are temporarily unavailable. For now, switch payments to <em>offline / manual</em> on this appointment so visitors can still book.
            </div>
          )}
        </div>

        <p className="text-[10px] uppercase tracking-widest text-[#82828c] mt-5">Toggles</p>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <ToggleRow label="Active (bookable)" v={form.active} onChange={(v) => setForm({ ...form, active: v })} testid="apt-active-toggle" />
          <ToggleRow label="Requires approval" v={form.requires_approval} onChange={(v) => setForm({ ...form, requires_approval: v })} testid="apt-approval-toggle" />
          <ToggleRow label="Phone number required" v={form.phone_required} onChange={(v) => setForm({ ...form, phone_required: v })} testid="apt-phone-required-toggle" />
          <ToggleRow label="Allow reschedule" v={form.allow_reschedule} onChange={(v) => setForm({ ...form, allow_reschedule: v })} />
          <ToggleRow label="Allow cancel" v={form.allow_cancel} onChange={(v) => setForm({ ...form, allow_cancel: v })} />
          <ToggleRow label="Auto prep task" v={form.auto_create_prep_task} onChange={(v) => setForm({ ...form, auto_create_prep_task: v })} />
          <ToggleRow label="Auto follow-up task" v={form.auto_create_followup_task} onChange={(v) => setForm({ ...form, auto_create_followup_task: v })} />
        </div>

        <DialogFooter className="mt-4">
          <button onClick={submit} className="bloq-btn-primary" data-testid="apt-save">{apt ? "Save changes" : "Create"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =====================================================================
 *  AVAILABILITY (schedules, rules, blocked time)
 * ===================================================================== */
function AvailabilityPanel({ workspace, user }) {
  const userTz = user?.timezone || workspace?.timezone || userBrowserTimezone();
  const [schedules, setSchedules] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [showBlock, setShowBlock] = useState(false);
  const load = async () => {
    if (!workspace) return;
    const [s, b] = await Promise.all([
      api.get(`/booking/availability/schedules`, { params: { workspace_id: workspace.workspace_id } }),
      api.get(`/booking/availability/blocked`, { params: { workspace_id: workspace.workspace_id } }),
    ]);
    setSchedules(s.data); setBlocked(b.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);
  const delSchedule = async (s) => {
    if (s.is_default) { toast.error("Cannot delete the default schedule"); return; }
    if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
    try { await api.delete(`/booking/availability/schedules/${s.schedule_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const duplicateSchedule = async (s) => {
    try {
      await api.post(`/booking/availability/schedules/${s.schedule_id}/duplicate`);
      toast.success(`Duplicated "${s.name}"`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#4a4a52]">Define your weekly availability + block out specific times. Times default to your timezone ({userTz}).</p>
        <button onClick={() => { setEdit(null); setShow(true); }} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-schedule-btn">
          <Plus className="w-4 h-4" /> New schedule
        </button>
      </div>
      {schedules.length === 0 ? (
        <EmptyCard icon={Clock} title="No schedules" desc="Create your first weekly availability." />
      ) : (
        <div className="flex flex-col gap-3">
          {schedules.map((s) => (
            <div key={s.schedule_id} className="bloq-card p-5" data-testid={`schedule-${s.schedule_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="bloq-headline text-lg text-[#005e7a]">{s.name} {s.is_default && <span className="bloq-pill ml-2 text-[10px]">Default</span>}</p>
                  <p className="text-xs text-[#82828c]">{s.timezone}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setEdit(s); setShow(true); }} className="bloq-btn-secondary text-xs px-3 py-1.5">Edit</button>
                  <button onClick={() => duplicateSchedule(s)} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid={`duplicate-schedule-${s.schedule_id}`}>Duplicate</button>
                  <button onClick={() => delSchedule(s)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#c43838]">Delete</button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {DAYS.map((d) => {
                  const rule = s.rules?.find((r) => r.day_of_week === d.v && r.active);
                  return (
                    <div key={d.v} className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-[#82828c]">{d.l}</p>
                      <p className="text-xs mt-1 font-medium">{rule ? `${rule.start_time}–${rule.end_time}` : "—"}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bloq-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="bloq-headline text-lg text-[#005e7a]">Blocked time</p>
          <button onClick={() => setShowBlock(true)} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="add-block-btn">
            <Plus className="w-3 h-3" /> Block range
          </button>
        </div>
        {blocked.length === 0 ? <p className="text-sm text-[#82828c]">No blocked time. Use this to mark vacations or focus blocks.</p> : (
          <div className="flex flex-col gap-2">
            {blocked.map((b) => (
              <div key={b.blocked_time_id} className="flex items-center justify-between bg-[#f5efe6] p-3 rounded-md" data-testid={`blocked-${b.blocked_time_id}`}>
                <div>
                  <p className="text-sm font-medium">{b.reason || "Blocked"}</p>
                  <p className="text-xs text-[#82828c]">
                    {formatInTz(b.start_time_utc, userTz, { dateStyle: "medium", timeStyle: "short" })}
                    {" → "}
                    {formatInTz(b.end_time_utc, userTz, { dateStyle: "medium", timeStyle: "short" })}
                    <span className="ml-2 text-[10px] uppercase tracking-widest">{userTz}</span>
                  </p>
                </div>
                <button onClick={async () => { await api.delete(`/booking/availability/blocked/${b.blocked_time_id}`); toast.success("Removed"); load(); }} className="bloq-btn-secondary text-xs px-2 py-1.5 text-[#c43838]"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ScheduleDialog open={show} onOpenChange={setShow} schedule={edit} workspace={workspace} defaultTz={userTz} onSaved={() => { setShow(false); setEdit(null); load(); }} />
      <BlockTimeDialog open={showBlock} onOpenChange={setShowBlock} workspace={workspace} defaultTz={userTz} onSaved={() => { setShowBlock(false); load(); }} />

      {/* Bundle A — Calendar settings sub-section. Lives at the bottom of
          the Availability tab so users find calendar conflict sources in
          the same place they manage weekly hours + blocked time. */}
      <CalendarConnectionsPanel workspace={workspace} userTimezone={userTz} />
    </div>
  );
}

function ScheduleDialog({ open, onOpenChange, schedule, workspace, defaultTz, onSaved }) {
  const fallbackTz = defaultTz || userBrowserTimezone();
  const [name, setName] = useState("");
  const [tz, setTz] = useState(fallbackTz);
  const [isDefault, setIsDefault] = useState(false);
  const [rules, setRules] = useState(
    DAYS.map((d) => ({ day_of_week: d.v, start_time: "09:00", end_time: "17:00", active: d.v < 6 }))
  );
  useEffect(() => {
    if (schedule) {
      setName(schedule.name); setTz(schedule.timezone || fallbackTz); setIsDefault(schedule.is_default || false);
      setRules(DAYS.map((d) => {
        const r = schedule.rules?.find((x) => x.day_of_week === d.v);
        return r ? { ...r } : { day_of_week: d.v, start_time: "09:00", end_time: "17:00", active: false };
      }));
    } else {
      setName(""); setTz(fallbackTz); setIsDefault(false);
      setRules(DAYS.map((d) => ({ day_of_week: d.v, start_time: "09:00", end_time: "17:00", active: d.v < 6 })));
    }
  }, [schedule, open, fallbackTz]);
  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    try {
      if (schedule) {
        await api.patch(`/booking/availability/schedules/${schedule.schedule_id}`, { name, timezone: tz, is_default: isDefault, rules });
      } else {
        await api.post(`/booking/availability/schedules`, { workspace_id: workspace.workspace_id, name, timezone: tz, is_default: isDefault, rules });
      }
      toast.success(schedule ? "Updated" : "Created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="schedule-dialog">
        <DialogHeader><DialogTitle>{schedule ? "Edit schedule" : "New schedule"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Name" full><input className="bloq-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="schedule-name" /></Field>
          <Field label="Timezone">
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-60">{COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <ToggleRow label="Make this the default" v={isDefault} onChange={setIsDefault} />
        </div>
        <div className="mt-4">
          <p className="text-xs uppercase tracking-widest text-[#82828c] mb-2">Weekly hours</p>
          <div className="flex flex-col gap-2">
            {rules.map((r, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#f5efe6] p-2.5 rounded-md">
                <Switch checked={r.active} onCheckedChange={(v) => setRules(rules.map((x, j) => j === i ? { ...x, active: v } : x))} />
                <span className="w-10 text-sm font-medium">{DAYS[i].l}</span>
                <input type="time" disabled={!r.active} className="bloq-input text-sm py-1 max-w-[120px]" value={r.start_time} onChange={(e) => setRules(rules.map((x, j) => j === i ? { ...x, start_time: e.target.value } : x))} />
                <span className="text-xs text-[#82828c]">to</span>
                <input type="time" disabled={!r.active} className="bloq-input text-sm py-1 max-w-[120px]" value={r.end_time} onChange={(e) => setRules(rules.map((x, j) => j === i ? { ...x, end_time: e.target.value } : x))} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="mt-4"><button onClick={submit} className="bloq-btn-primary" data-testid="schedule-save">{schedule ? "Save" : "Create"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * BlockTimeDialog — Bundle A.
 *
 * Replaces the legacy 3x window.prompt() flow. Lets the user pick a
 * date range and a time range in THEIR timezone (or any other zone),
 * with a "Block whole day(s)" shortcut. Internally everything is
 * converted to UTC ISO strings before POSTing to /booking/availability/blocked.
 *
 * UTC conversion uses an offset-derivation trick: we build an
 * Intl.DateTimeFormat in the target zone, ask it for the parts of a
 * candidate UTC instant, and shift until we find the UTC instant that
 * renders to the wall-time the user typed. This avoids a date-fns-tz
 * dependency and handles DST correctly.
 */
function BlockTimeDialog({ open, onOpenChange, workspace, defaultTz, onSaved }) {
  const fallbackTz = defaultTz || userBrowserTimezone();
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [allDay, setAllDay] = useState(false);
  const [tz, setTz] = useState(fallbackTz);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset to sensible defaults whenever the dialog re-opens.
  useEffect(() => {
    if (!open) return;
    const t = todayIso();
    setStartDate(t); setEndDate(t);
    setStartTime("09:00"); setEndTime("17:00");
    setAllDay(false);
    setTz(fallbackTz);
    setReason("");
  }, [open, fallbackTz]);

  // Convert (date "YYYY-MM-DD", time "HH:MM", zone) → UTC ISO instant.
  // Strategy: take the wall-time as if it were UTC, then ask Intl what
  // that UTC instant LOOKS like in the target zone — the delta is the
  // zone's offset for that moment (DST-aware). Subtract it to land on
  // the right UTC instant.
  const wallToUtcIso = (dateStr, timeStr, zone) => {
    const [y, mo, d] = dateStr.split("-").map(Number);
    const [h, mi] = timeStr.split(":").map(Number);
    if (!y || !mo || !d) return null;
    // Pretend the wall-time is UTC for one second.
    const naive = Date.UTC(y, mo - 1, d, h || 0, mi || 0, 0);
    // Format that instant in the target zone and read the parts back.
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: zone, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const parts = Object.fromEntries(fmt.formatToParts(new Date(naive)).map((p) => [p.type, p.value]));
    const tzWall = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour === "24" ? "0" : parts.hour),
      Number(parts.minute), Number(parts.second),
    );
    const offsetMs = tzWall - naive;
    return new Date(naive - offsetMs).toISOString();
  };

  // For "all day", lock to 00:00→24:00 across the inclusive date range.
  const computeUtcWindow = () => {
    const startT = allDay ? "00:00" : startTime;
    const endT = allDay ? "00:00" : endTime;
    let endD = endDate;
    if (allDay) {
      // 23:59:59.999 of the end date is awkward; instead push end date by +1 day at 00:00.
      const e = new Date(endDate + "T00:00:00Z");
      e.setUTCDate(e.getUTCDate() + 1);
      endD = e.toISOString().slice(0, 10);
    }
    const startUtc = wallToUtcIso(startDate, startT, tz);
    const endUtc = wallToUtcIso(endD, endT, tz);
    return { startUtc, endUtc };
  };

  // Lightweight live preview so the user can confirm the UTC window
  // before submitting (catches "I picked the wrong timezone" cases).
  const preview = (() => {
    try {
      const { startUtc, endUtc } = computeUtcWindow();
      if (!startUtc || !endUtc) return null;
      return { startUtc, endUtc };
    } catch { return null; }
  })();

  const submit = async () => {
    if (!workspace) return;
    if (!startDate || !endDate) { toast.error("Pick a start and end date"); return; }
    if (!allDay && (!startTime || !endTime)) { toast.error("Pick a start and end time"); return; }
    const { startUtc, endUtc } = computeUtcWindow();
    if (!startUtc || !endUtc) { toast.error("Invalid date/time"); return; }
    if (new Date(startUtc) >= new Date(endUtc)) {
      toast.error("End must be after start");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/booking/availability/blocked`, {
        workspace_id: workspace.workspace_id,
        start_time_utc: startUtc,
        end_time_utc: endUtc,
        reason: reason.trim(),
        // We also store the original wall-time inputs so the list view
        // can display them in the originally-chosen zone if needed.
        source_timezone: tz,
        source_start_local: allDay ? `${startDate}T00:00` : `${startDate}T${startTime}`,
        source_end_local: allDay ? `${endDate}T23:59` : `${endDate}T${endTime}`,
        all_day: allDay,
      });
      toast.success("Time blocked");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="block-time-dialog">
        <DialogHeader>
          <DialogTitle>Block time</DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {/* All-day shortcut */}
          <div className="flex items-center gap-3 bg-[#f5efe6] p-3 rounded-md">
            <Switch
              checked={allDay}
              onCheckedChange={setAllDay}
              data-testid="block-allday-toggle"
            />
            <div className="flex-1">
              <p className="text-sm font-medium">Block whole day(s)</p>
              <p className="text-[11px] text-[#82828c]">
                Blocks the full 24 hours of each selected date in the chosen timezone.
              </p>
            </div>
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <Field label={allDay ? "Day" : "Start date"}>
              <input
                type="date"
                className="bloq-input"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  // Auto-bump end date if it's now before start.
                  if (e.target.value > endDate) setEndDate(e.target.value);
                }}
                data-testid="block-start-date"
              />
            </Field>
            <Field label={allDay ? "End day (inclusive)" : "End date"}>
              <input
                type="date"
                className="bloq-input"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="block-end-date"
              />
            </Field>
          </div>

          {/* Time range — hidden when all-day */}
          {!allDay && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start time">
                <input
                  type="time"
                  className="bloq-input"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  data-testid="block-start-time"
                />
              </Field>
              <Field label="End time">
                <input
                  type="time"
                  className="bloq-input"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  data-testid="block-end-time"
                />
              </Field>
            </div>
          )}

          {/* Timezone */}
          <Field label="Timezone">
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger data-testid="block-tz-trigger"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-60">
                {COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {/* Reason */}
          <Field label="Reason (optional)">
            <input
              type="text"
              className="bloq-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Vacation, focus block, conference…"
              maxLength={200}
              data-testid="block-reason"
            />
          </Field>

          {/* Live UTC preview */}
          {preview && (
            <div className="text-[11px] text-[#82828c] bg-[#f5efe6] p-2.5 rounded-md" data-testid="block-utc-preview">
              <p className="font-medium text-[#1a1a1f] mb-1">UTC window (saved):</p>
              <p>{new Date(preview.startUtc).toISOString().replace("T", " ").slice(0, 16)} UTC</p>
              <p>→ {new Date(preview.endUtc).toISOString().replace("T", " ").slice(0, 16)} UTC</p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <button
            onClick={() => onOpenChange(false)}
            className="bloq-btn-secondary text-sm"
            disabled={saving}
            data-testid="block-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="bloq-btn-primary"
            data-testid="block-save"
          >
            {saving ? "Saving…" : "Block time"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =====================================================================
 *  FORMS
 * ===================================================================== */
function FormsPanel({ workspace }) {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [status, setStatus] = useState("active");
  const bulk = useBulkSelect();
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = async () => {
    if (!workspace) return;
    const r = await api.get(`/booking/forms`, { params: { workspace_id: workspace.workspace_id, status } });
    setRows(r.data);
    bulk.clear();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace, status]);
  const archive = async (f) => {
    if (!window.confirm(`Archive "${f.name}"?`)) return;
    try {
      await api.delete(`/booking/forms/${f.form_id}`);
      toast.success("Archived — switch the filter to 'Archived' to find it."); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async (f) => {
    try {
      await api.post(`/booking/forms/${f.form_id}/restore`);
      toast.success("Restored"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const hardDelete = async (f) => {
    if (!window.confirm(`Permanently delete "${f.name}"? This cannot be undone. Any appointment types using this form will be detached.`)) return;
    if (!window.confirm(`Are you absolutely sure? This deletes "${f.name}" forever.`)) return;
    try {
      await api.delete(`/booking/forms/${f.form_id}`, { params: { hard: true } });
      toast.success("Deleted permanently"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const duplicate = async (f) => {
    try {
      await api.post(`/booking/forms/${f.form_id}/duplicate`);
      toast.success(`Duplicated "${f.name}"`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#4a4a52]">Intake questions invitees answer when booking.</p>
        <button onClick={() => { setEdit(null); setShow(true); }} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-form-btn">
          <Plus className="w-4 h-4" /> New form
        </button>
      </div>
      <StatusFilter value={status} onChange={setStatus} options={["active", "archived", "all"]} count={rows.length} testid="form-status-filter" />
      {rows.length === 0 ? <EmptyCard icon={FileText} title={`No ${status === "all" ? "" : status + " "}intake forms`} desc={status === "archived" ? "Archive a form to see it here." : "Create custom intake questions for any booking link."} /> : (
        <>
          <BulkSelectToolbar
            selectedCount={bulk.count}
            totalVisible={rows.length}
            onSelectAll={() => bulk.selectAll(rows.map((f) => f.form_id))}
            onClear={bulk.clear}
            onDelete={() => setBulkConfirm(true)}
            itemLabel="form"
            busy={bulkBusy}
            testidPrefix="forms-bulk"
          />
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {rows.map((f) => {
              const checked = bulk.isSelected(f.form_id);
              return (
                <div key={f.form_id} className={`bloq-card p-5 flex flex-col gap-3 ${f.archived_at ? "opacity-60" : ""} ${checked ? "ring-2 ring-[#005e7a]" : ""}`} data-testid={`form-${f.form_id}`}>
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <button
                          type="button"
                          onClick={() => bulk.toggle(f.form_id)}
                          className="p-1 -m-1 mt-0.5 flex-shrink-0"
                          aria-label={checked ? "Unselect form" : "Select form"}
                          data-testid={`forms-bulk-row-${f.form_id}`}
                        >
                          {checked
                            ? <CheckSquare className="w-4 h-4 text-[#005e7a]" />
                            : <Square className="w-4 h-4 text-[#82828c]" />}
                        </button>
                        <p className="bloq-headline text-lg min-w-0 break-words text-[#005e7a]">{f.name}</p>
                      </div>
                      {f.archived_at && <span className="bloq-pill text-[10px] uppercase tracking-wider font-semibold bg-[#1a1a1f]/10 text-[#4a4a52] shrink-0">Archived</span>}
                    </div>
                    <p className="text-xs text-[#82828c] mt-1">{f.fields?.length || 0} field{(f.fields?.length || 0) === 1 ? "" : "s"}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => { setEdit(f); setShow(true); }} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid={`edit-form-${f.form_id}`}>Edit</button>
                    <button onClick={() => duplicate(f)} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid={`duplicate-form-${f.form_id}`}>Duplicate</button>
                    {f.archived_at ? (
                      <button onClick={() => restore(f)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#005e7a]" data-testid={`restore-form-${f.form_id}`}>Restore</button>
                    ) : (
                      <button onClick={() => archive(f)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#82828c]" data-testid={`archive-form-${f.form_id}`} title="Archive — hidden but recoverable">Archive</button>
                    )}
                    <button onClick={() => hardDelete(f)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#c43838] inline-flex items-center gap-1" data-testid={`delete-form-${f.form_id}`} title="Permanently delete — cannot be undone">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      <BulkDeleteConfirmDialog
        open={bulkConfirm}
        onClose={() => setBulkConfirm(false)}
        onConfirm={async () => {
          setBulkBusy(true);
          try {
            const r = await api.post(`/booking/forms/bulk-delete`, {
              workspace_id: workspace.workspace_id,
              ids: bulk.ids,
            });
            toast.success(`Deleted ${r.data.deleted} form(s)`);
            setBulkConfirm(false);
            load();
          } catch (e) {
            toast.error(formatApiError(e) || "Bulk delete failed");
          } finally {
            setBulkBusy(false);
          }
        }}
        count={bulk.count}
        itemLabel="form"
        description="Permanently delete the selected intake forms. Any appointment types using them will be detached. Past booking submissions remain intact."
        busy={bulkBusy}
        testidPrefix="forms-bulk-delete"
      />
      <FormDialog open={show} onOpenChange={setShow} form={edit} workspace={workspace} onSaved={() => { setShow(false); setEdit(null); load(); }} />
    </div>
  );
}

function FormDialog({ open, onOpenChange, form, workspace, onSaved }) {
  const [name, setName] = useState("");
  const [fields, setFields] = useState([
    { field_type: "short_text", label: "Name", required: true },
    { field_type: "email", label: "Email", required: true },
  ]);
  // Drag-and-drop reorder state. We track the source index while a row is
  // being dragged so the drop handler knows where to splice.
  const [dragIndex, setDragIndex] = useState(null);
  useEffect(() => {
    if (form) { setName(form.name); setFields((form.fields || []).map((f) => ({ ...f }))); }
    else { setName(""); setFields([
      { field_type: "short_text", label: "Name", required: true },
      { field_type: "email", label: "Email", required: true },
    ]); }
  }, [form, open]);
  const addField = () => setFields([...fields, { field_type: "short_text", label: "New question", required: false }]);
  const moveField = (from, to) => {
    if (from === to || to < 0 || to >= fields.length) return;
    setFields((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const submit = async () => {
    if (!name.trim()) { toast.error("Form name required"); return; }
    const cleaned = fields.map((f, i) => ({ ...f, sort_order: i }));
    try {
      if (form) await api.patch(`/booking/forms/${form.form_id}`, { name, fields: cleaned });
      else await api.post(`/booking/forms`, { workspace_id: workspace.workspace_id, name, fields: cleaned });
      toast.success(form ? "Updated" : "Created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="form-dialog">
        <DialogHeader><DialogTitle>{form ? "Edit intake form" : "New intake form"}</DialogTitle></DialogHeader>

        {/* Single-column layout — form name → add-field button → reorder hint
            → the list of field rows. Keeps everything flowing naturally
            without the grid blank space the legacy layout had. */}
        <div>
          <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">Form name <span className="text-[#82828c] normal-case tracking-normal">(internal — not shown to invitees)</span></label>
          <input className="bloq-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="form-name" />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
          <p className="text-xs text-[#82828c] inline-flex items-center gap-1.5">
            Drag <GripVertical className="w-3.5 h-3.5" /> to reorder, or use the up/down arrows.
          </p>
          <button onClick={addField} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-2" data-testid="form-add-field">
            <Plus className="w-3 h-3" /> Add field
          </button>
        </div>

        <div className="mt-2 flex flex-col gap-2">
          {fields.map((f, i) => (
            <FormFieldRow
              key={i}
              index={i}
              total={fields.length}
              field={f}
              onChange={(patch) => setFields(fields.map((x, j) => j === i ? { ...x, ...patch } : x))}
              onDelete={() => setFields(fields.filter((_, j) => j !== i))}
              onMoveUp={() => moveField(i, i - 1)}
              onMoveDown={() => moveField(i, i + 1)}
              dragIndex={dragIndex}
              setDragIndex={setDragIndex}
              onDrop={(from, to) => moveField(from, to)}
            />
          ))}
        </div>
        <DialogFooter className="mt-4"><button onClick={submit} className="bloq-btn-primary" data-testid="form-save">{form ? "Save" : "Create"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * FormFieldRow — single intake-form field configurator. Each row owns its
 * own local input state for the options textarea so the parent's state
 * round-trip (join → split → filter) doesn't strip user-typed commas
 * mid-edit. We sync to the parent on blur + when the user types a newline.
 *
 * Drag-and-drop reorder: the row is `draggable`, and the parent FormDialog
 * owns `dragIndex` so we know the source. On drop we call `onDrop(from, to)`.
 * Keyboard users get explicit up/down chevrons.
 */
function FormFieldRow({ index, total, field, onChange, onDelete, onMoveUp, onMoveDown, dragIndex, setDragIndex, onDrop }) {
  const hasOptions = ["dropdown", "radio", "multi_checkbox"].includes(field.field_type);
  // Keep a draft string locally so commas/spaces aren't filtered while typing.
  const [optionsDraft, setOptionsDraft] = useState((field.options || []).join("\n"));
  const [dragOver, setDragOver] = useState(false);
  useEffect(() => {
    // External updates (e.g. switching field_type, editing different field) refresh the draft.
    setOptionsDraft((field.options || []).join("\n"));
    // eslint-disable-next-line
  }, [field.field_type, index]);
  const commitOptions = (raw) => {
    const opts = raw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    onChange({ options: opts });
  };
  const handleDragStart = (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires data for the drag to start.
    try { e.dataTransfer.setData("text/plain", String(index)); } catch (_) { /* noop */ }
  };
  const handleDragOver = (e) => {
    if (dragIndex == null || dragIndex === index) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!dragOver) setDragOver(true);
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (dragIndex == null || dragIndex === index) return;
    onDrop(dragIndex, index);
    setDragIndex(null);
  };
  const handleDragEnd = () => { setDragIndex(null); setDragOver(false); };
  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      className={`bg-[#f5efe6] p-3 rounded-md grid grid-cols-12 gap-2 items-start transition-colors
        ${dragIndex === index ? "opacity-40" : ""}
        ${dragOver ? "ring-2 ring-[#005e7a] bg-[#005e7a]/5" : ""}`}
      data-testid={`form-field-row-${index}`}
    >
      <div className="col-span-12 flex items-center gap-1 text-[#82828c]">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:text-[#005e7a]"
          title="Drag to reorder"
          data-testid={`form-field-drag-${index}`}
          // The button itself isn't draggable — the parent <div> is — but
          // making this the visual handle lets users see where to grab.
          onMouseDown={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onMoveUp}
          disabled={index === 0}
          className="p-1 hover:text-[#005e7a] disabled:opacity-25 disabled:cursor-not-allowed"
          title="Move up"
          data-testid={`form-field-up-${index}`}
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={index === total - 1}
          className="p-1 hover:text-[#005e7a] disabled:opacity-25 disabled:cursor-not-allowed"
          title="Move down"
          data-testid={`form-field-down-${index}`}
        >
          <ChevronDown className="w-4 h-4" />
        </button>
        <span className="text-[10px] uppercase tracking-wider ml-1">Question {index + 1}</span>
      </div>
      <Select value={field.field_type} onValueChange={(v) => onChange({ field_type: v })}>
        <SelectTrigger className="col-span-3" data-testid={`form-field-type-${index}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="short_text">Short text</SelectItem>
          <SelectItem value="long_text">Long text</SelectItem>
          <SelectItem value="email">Email</SelectItem>
          <SelectItem value="phone">Phone</SelectItem>
          <SelectItem value="number">Number</SelectItem>
          <SelectItem value="price">Price</SelectItem>
          <SelectItem value="address">Address</SelectItem>
          <SelectItem value="url">URL</SelectItem>
          <SelectItem value="date">Date</SelectItem>
          <SelectItem value="dropdown">Dropdown</SelectItem>
          <SelectItem value="radio">Radio buttons</SelectItem>
          <SelectItem value="checkbox">Checkbox (single)</SelectItem>
          <SelectItem value="multi_checkbox">Multi-checkbox</SelectItem>
          <SelectItem value="consent">Consent checkbox</SelectItem>
          <SelectItem value="rich_text">Rich text (display)</SelectItem>
        </SelectContent>
      </Select>
      <input
        className="bloq-input col-span-6"
        placeholder={field.field_type === "rich_text" ? "Section heading (optional)" : "Question label"}
        value={field.label}
        onChange={(e) => onChange({ label: e.target.value })}
      />
      <label className="col-span-2 text-xs flex items-center gap-2 pt-2" title="Required field">
        <input
          type="checkbox"
          checked={!!field.required}
          onChange={(e) => onChange({ required: e.target.checked })}
          data-testid={`form-field-required-${index}`}
          disabled={field.field_type === "rich_text"}
        />
        Required
      </label>
      <button onClick={onDelete} className="col-span-1 text-[#c43838] pt-2" title="Delete field" data-testid={`form-field-delete-${index}`}><Trash2 className="w-4 h-4 mx-auto" /></button>

      {/* Options editor for dropdown / radio / multi_checkbox */}
      {hasOptions && (
        <div className="col-span-12">
          <textarea
            className="bloq-input text-xs font-mono min-h-[64px]"
            placeholder="One option per line — e.g.
Option A
Option B"
            value={optionsDraft}
            onChange={(e) => setOptionsDraft(e.target.value)}
            onBlur={() => commitOptions(optionsDraft)}
            data-testid={`form-field-options-${index}`}
          />
          {field.field_type === "radio" && (
            <label className="flex items-center gap-2 text-xs mt-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!field.allow_other}
                onChange={(e) => onChange({ allow_other: e.target.checked })}
                data-testid={`form-field-allow-other-${index}`}
              />
              Add an "Other" option with a fill-in text box
            </label>
          )}
        </div>
      )}
      {/* Rich-text body editor — full WYSIWYG with bold/italic/h1-h3/lists/
          link/image. Authored content is admin-trusted HTML, rendered with
          RichTextView on the public booking page. */}
      {field.field_type === "rich_text" && (
        <div className="col-span-12">
          <RichTextEditor
            value={field.body || ""}
            onChange={(html) => onChange({ body: html })}
            placeholder="Paragraph text shown to the visitor (no input collected)."
            testid={`form-field-body-${index}`}
          />
        </div>
      )}
      {/* Help text — works for any input field */}
      {field.field_type !== "rich_text" && (
        <input
          className="bloq-input col-span-12 text-xs"
          placeholder="Help text shown under the field (optional)"
          value={field.help_text || ""}
          onChange={(e) => onChange({ help_text: e.target.value })}
        />
      )}
    </div>
  );
}

/* =====================================================================
 *  ADD-ONS + COUPONS + ANALYTICS  (lean panels)
 * ===================================================================== */
function AddonsPanel({ workspace }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", price_delta_amount: 0, duration_delta_minutes: 0 });
  const load = async () => {
    if (!workspace) return;
    setRows((await api.get(`/booking/addons`, { params: { workspace_id: workspace.workspace_id } })).data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);
  const create = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    await api.post(`/booking/addons`, { ...form, workspace_id: workspace.workspace_id });
    setForm({ name: "", price_delta_amount: 0, duration_delta_minutes: 0 }); toast.success("Created"); load();
  };
  return (
    <div className="flex flex-col gap-5">
      <div className="bloq-card p-5">
        <p className="bloq-headline text-lg mb-3 text-[#005e7a]">Quick-create add-on</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input className="bloq-input sm:col-span-2" placeholder="Add-on name (e.g. 15-min extension)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="addon-name" />
          <input type="number" className="bloq-input" placeholder="Price delta" value={form.price_delta_amount} onChange={(e) => setForm({ ...form, price_delta_amount: Number(e.target.value) })} />
          <input type="number" className="bloq-input" placeholder="Duration delta (min)" value={form.duration_delta_minutes} onChange={(e) => setForm({ ...form, duration_delta_minutes: Number(e.target.value) })} />
        </div>
        <button onClick={create} className="bloq-btn-primary mt-3" data-testid="addon-create">Create add-on</button>
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((a) => (
          <div key={a.addon_id} className="bloq-card p-4 flex items-center justify-between" data-testid={`addon-${a.addon_id}`}>
            <div>
              <p className="font-medium">{a.name}</p>
              <p className="text-xs text-[#82828c]">{a.price_delta_amount > 0 ? `+$${a.price_delta_amount}` : a.price_delta_amount < 0 ? `-$${Math.abs(a.price_delta_amount)}` : "Free"} · {a.duration_delta_minutes > 0 ? `+${a.duration_delta_minutes}m` : `${a.duration_delta_minutes}m`}</p>
            </div>
            <button onClick={async () => { await api.delete(`/booking/addons/${a.addon_id}`); toast.success("Deleted"); load(); }} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#c43838]"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-[#82828c]">No add-ons yet.</p>}
      </div>
    </div>
  );
}

function CouponsPanel({ workspace }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: 10 });
  const load = async () => {
    if (!workspace) return;
    setRows((await api.get(`/booking/coupons`, { params: { workspace_id: workspace.workspace_id } })).data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);
  const create = async () => {
    if (!form.code.trim()) { toast.error("Code required"); return; }
    try {
      await api.post(`/booking/coupons`, { ...form, workspace_id: workspace.workspace_id });
      toast.success("Created"); setForm({ code: "", discount_type: "percent", discount_value: 10 }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-col gap-5">
      <div className="bloq-card p-5">
        <p className="bloq-headline text-lg mb-3 text-[#005e7a]">Create coupon</p>
        <div className="flex items-center gap-2">
          <input className="bloq-input uppercase" placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-code" />
          <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="fixed">Fixed</SelectItem></SelectContent>
          </Select>
          <input type="number" className="bloq-input w-24" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })} />
          <button onClick={create} className="bloq-btn-primary" data-testid="coupon-create">Create</button>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((c) => (
          <div key={c.coupon_id} className="bloq-card p-4 flex items-center justify-between" data-testid={`coupon-${c.coupon_id}`}>
            <div>
              <code className="font-mono font-medium">{c.code}</code>
              <p className="text-xs text-[#82828c]">{c.discount_type === "percent" ? `${c.discount_value}% off` : `$${c.discount_value} off`} · {c.redemptions_count}/{c.max_redemptions || "∞"} redeemed</p>
            </div>
            <button onClick={async () => { await api.delete(`/booking/coupons/${c.coupon_id}`); toast.success("Deleted"); load(); }} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#c43838]"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-[#82828c]">No coupons yet.</p>}
      </div>
    </div>
  );
}

function AnalyticsPanel({ workspace }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!workspace) return;
    api.get(`/booking/analytics`, { params: { workspace_id: workspace.workspace_id } }).then((r) => setData(r.data));
  }, [workspace]);
  if (!data) return <p className="text-sm text-[#82828c]">Loading…</p>;
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <StatCard label="Total bookings" value={data.total} icon={BarChart3} />
      <StatCard label="Cancel rate" value={`${data.cancel_rate_pct}%`} icon={XCircle} />
      <StatCard label="Reschedule rate" value={`${data.reschedule_rate_pct}%`} icon={Clock} />
      <div className="bloq-card p-5 lg:col-span-3">
        <p className="bloq-headline text-lg mb-3 text-[#005e7a]">By status</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(data.by_status || {}).map(([k, v]) => (
            <div key={k} className="bg-[#f5efe6] p-3 rounded-md">
              <p className="text-xs text-[#82828c] uppercase">{k}</p>
              <p className="text-xl font-semibold">{v}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="bloq-card p-5 lg:col-span-2">
        <p className="bloq-headline text-lg mb-3 text-[#005e7a]">By appointment type</p>
        {data.by_appointment_type.length === 0 ? <p className="text-sm text-[#82828c]">No data.</p> : data.by_appointment_type.map((row) => (
          <div key={row.appointment_type_id} className="flex justify-between py-2 border-b border-[rgba(26,26,31,0.05)] last:border-0">
            <span className="text-sm">{row.name}</span><span className="font-medium">{row.count}</span>
          </div>
        ))}
      </div>
      <div className="bloq-card p-5">
        <p className="bloq-headline text-lg mb-3 text-[#005e7a]">By source</p>
        {Object.entries(data.by_source || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between py-2 border-b border-[rgba(26,26,31,0.05)] last:border-0">
            <span className="text-sm">{k}</span><span className="font-medium">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =====================================================================
 *  Reusable bits
 * ===================================================================== */
function Field({ label, children, full, help }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">{label}</label>
      {children}
      {help && <p className="text-[10px] text-[#82828c] mt-1 leading-snug">{help}</p>}
    </div>
  );
}

/**
 * StatusFilter — segmented control rendered above each list of bookings /
 * appointments / forms. Drives the `status` query param sent to the
 * listing endpoints (active | inactive | archived | all). Defaults align
 * with what a host normally wants to see: just the active items.
 *
 *   props.options — controls which segments render. Forms don't have an
 *                   `active` field, so they pass ["active","archived","all"].
 *   props.count   — appears as a subtle "(N)" suffix to the active segment,
 *                   so the user can see how the filter is whittling.
 */
const STATUS_LABEL = {
  active: "Active",
  inactive: "Inactive",
  archived: "Archived",
  all: "All",
};
function StatusFilter({ value, onChange, options, count, testid }) {
  return (
    <div className="inline-flex items-center gap-1 bg-[#f5efe6] p-1 rounded-md self-start" data-testid={testid}>
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={`px-3 py-1 text-xs uppercase tracking-wider font-semibold rounded transition-colors
              ${active ? "bg-white text-[#005e7a] shadow-sm" : "text-[#82828c] hover:text-[#1a1a1f]"}`}
            data-testid={testid ? `${testid}-${o}` : undefined}
            aria-pressed={active}
          >
            {STATUS_LABEL[o]}{active && typeof count === "number" ? ` (${count})` : ""}
          </button>
        );
      })}
    </div>
  );
}

function ToggleRow({ label, v, onChange, testid }) {
  return (
    <label className="flex items-center justify-between p-3 rounded-lg bg-[#f5efe6] col-span-1">
      <span className="text-sm">{label}</span>
      <Switch checked={v} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

function EmptyCard({ icon: Icon, title, desc }) {
  return (
    <div className="bloq-card p-10 text-center">
      <Icon className="w-10 h-10 mx-auto text-[#82828c] mb-3" />
      <p className="bloq-headline text-xl mb-1 text-[#005e7a]">{title}</p>
      <p className="text-sm text-[#82828c]">{desc}</p>
    </div>
  );
}
